<template>
  <div class="bg-[#f8f9fa] border border-gray-100 rounded-[8px] p-4 flex flex-col items-center gap-3 min-w-0 max-w-full">
    <div class="w-full flex items-center justify-between gap-3 border-b pb-1">
      <h5 class="font-bold text-xs uppercase tracking-widest">{{ heading || 'Live preview' }}</h5>
      <button
        type="button"
        class="text-[11px] font-semibold uppercase tracking-wide text-gray-600 hover:text-black underline underline-offset-2 disabled:opacity-50 disabled:cursor-wait"
        :disabled="isDownloading"
        @click="downloadPreview"
      >{{ isDownloading ? 'Preparing…' : 'Download' }}</button>
    </div>

    <!-- px/py leave room for the wall shadow below. This box is overflow-hidden (kept from an
         earlier rendering fix), so without the padding the shadow is simply clipped off — and the
         frame is sized from this element's CONTENT width, so the padding also shrinks the frame to
         suit rather than letting it run under the clip. -->
    <!-- The right/top padding below is asymmetric on purpose — it's spill room for the height
         label and wall shadow, which are drawn OUTSIDE the frame's own box (absolutely positioned)
         and so aren't counted when this box gets centered by the flex row above. On desktop the
         fixed 112px/80px spill room is a small fraction of the available width and goes unnoticed.
         On a narrow mobile screen it's a much bigger fraction of the total, which visually reads as
         the frame being pushed off-centre with its right edge clipped. Cut down (not removed —
         the label/shadow still need SOME room) only below the lg breakpoint this whole dialog
         already switches layout at; lg: and up keep the exact original desktop values. -->
    <!-- Fixed height, not min-height: this box's size must never depend on what's drawn inside it
         — the frame/artwork scale DOWN to fit this space (see uniformScale), never the other way
         around. A min-height let a bigger front-width/mount grow the box itself, which is exactly
         the "the live preview card's own size keeps changing" behaviour that was reported. -->
    <!-- Height is responsive too, not just MAX_PREVIEW_H everywhere: mobile's available WIDTH is
         so much narrower than desktop's that width is almost always the binding constraint there,
         leaving a fixed desktop-sized height box mostly empty air above/below a small, centred
         frame. availableH below measures whatever this actually resolves to at the current
         breakpoint, so the scale math adapts on its own — nothing else needs to change in sync. -->
    <div ref="containerRef" class="w-full min-w-0 flex items-center justify-center pl-4 pr-20 pt-6 pb-4 lg:pl-10 lg:pr-28 lg:pt-10 lg:pb-8 overflow-hidden h-[300px] lg:h-[420px]">

      <!-- STRETCHED CANVAS — a real 3D box, turned the way the client's reference shot is framed:
           camera to the LEFT and slightly ABOVE, so the right-hand return and a sliver of the top
           return are both in view and the slab of bar depth behind the painting is unmistakable.
           The turn is large on purpose (see STRETCH_YAW): a return only projects to depth *
           sin(yaw), so a shy angle leaves a sliver that reads as a border, not as depth.
           The drop-shadow sits on an OUTER wrapper, never on the rotated box — a filter forces its
           own element back to flat rendering and would collapse the 3D entirely. -->
      <div
        v-if="isStretchMode"
        class="relative shrink-0"
        :style="{ width: artWpx + 'px', height: artHpx + 'px' }"
      >
        <div
          class="absolute inset-0"
          :style="{
            // translateZ(0) appended (not a separate transform key — only one wins) for the same
            // iOS compositing-layer reason as the regular frame's wall-shadow wrapper below: an
            // element with `filter` can stop re-compositing when its children change until a
            // scroll/tap forces it. This element already had a transform, which may already have
            // been enough on its own, but pinning it explicitly removes any doubt.
            transform: `translate(${stretchOffsetX}px, ${stretchOffsetY}px) translateZ(0)`,
            // The shading lives out here on the wall, not on the bar, so the white sides stay white.
            // Three layers: a hairline contact shadow that draws the white bar's edge against the
            // light panel (without it the white side dissolves into the background), a near one,
            // and a broad soft one for how far the canvas stands off the wall.
            filter: 'drop-shadow(1px 1px 1px rgba(0,0,0,0.16)) drop-shadow(6px 9px 10px rgba(0,0,0,0.16)) drop-shadow(20px 30px 42px rgba(0,0,0,0.18))',
            transition: TR,
            WebkitBackfaceVisibility: 'hidden',
          }"
        >
          <div :style="{ perspective: stretchPerspectivePx + 'px', perspectiveOrigin: '50% 45%' }">
            <div :style="{
              position: 'relative',
              width: artWpx + 'px',
              height: artHpx + 'px',
              transformStyle: 'preserve-3d',
              transform: `rotateX(${STRETCH_PITCH}deg) rotateY(${-STRETCH_YAW}deg)`,
              transition: TR
            }">
              <!-- RIGHT RETURN. The hinge must sit ON the right edge, hence left:100% — with
                   right:0 the strip's own left edge lands at (width - depth), i.e. INSIDE the face,
                   so it swings back from within and the artwork hides it completely.
                   The sides are plain white canvas pulled round the bars, not the artwork. -->
              <div class="absolute top-0" :style="{
                left: '100%',
                width: stretchDepthPx + 'px',
                height: '100%',
                transformOrigin: 'left center',
                transform: 'rotateY(90deg)',
                backgroundColor: '#F9F8F4', // raw canvas off-white, per the client — not pure white
                transition: TR
              }">
                <!-- Only the faintest tone, so the side still reads as a separate plane from the top
                     while looking plainly white. The real depth cue is the shadow on the wall (see
                     the wrapper's filter). Overlay, never a filter: a filter on a face inside
                     preserve-3d pulls it out of the 3D scene. -->
                <div class="absolute inset-0" style="background:linear-gradient(90deg, rgba(0,0,0,0.015) 0%, rgba(0,0,0,0.045) 100%);"></div>
              </div>

              <!-- TOP RETURN. Same hinge rule: the strip sits ABOVE the face (top: -depth) and turns
                   down about its own bottom edge, so it hinges exactly on the artwork's top edge and
                   faces up rather than showing its underside. Plain white canvas as well. -->
              <div class="absolute left-0" :style="{
                top: (-stretchDepthPx) + 'px',
                width: '100%',
                height: stretchDepthPx + 'px',
                transformOrigin: 'center bottom',
                transform: 'rotateX(90deg)',
                backgroundColor: '#F9F8F4', // raw canvas off-white, per the client — not pure white
                transition: TR
              }">
              </div>

              <!-- FRONT FACE — the whole artwork. -->
              <div class="absolute inset-0 overflow-hidden bg-white">
                <img v-if="artworkImageSrc" :src="artworkImage" class="w-full h-full object-cover block" />
                <!-- Edge line where the painted face meets the white sides, so the two don't blur
                     into each other at the fold. -->
                <div class="absolute inset-0 pointer-events-none" style="box-shadow: inset 0 0 0 1px rgba(0,0,0,0.12);"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- DIMENSION CALLOUTS, angled to match the box's own turn. A stretch has no frame or
             mount to add, so these read the canvas's own declared width/height. Deliberately a
             SIBLING of the filtered/rotated wrapper above, not a child of it: the wrapper's
             drop-shadow filter would otherwise throw its own shadow onto these lines, and being
             outside the preserve-3d/rotate chain lets them stay flat 2D lines drawn at the right
             angle rather than actually living inside the 3D scene. Gets the SAME stretchOffsetX/Y
             translate as the wrapper above (not nested inside it) so the two stay co-located —
             both sit at local (0,0)..(artWpx,artHpx) before that shared shift.
             Earlier version used a flat, unrotated rectangle overlay here — right for a frame
             preview, wrong for a turned box, since the box's own top/right edges are visibly
             tilted by the yaw/pitch turn and a horizontal/vertical overlay didn't match them. This
             one instead projects the front face's 4 corners through the exact same rotation +
             perspective maths the box itself is drawn with (see stretchFrontCorners), then draws
             each arrow parallel to its own edge. -->
        <div class="absolute inset-0 pointer-events-none" :style="{ transform: `translate(${stretchOffsetX}px, ${stretchOffsetY}px)` }">
          <svg :width="artWpx" :height="artHpx" class="absolute inset-0 overflow-visible">
            <line :x1="stretchWidthArrow.x1" :y1="stretchWidthArrow.y1" :x2="stretchWidthArrow.x2" :y2="stretchWidthArrow.y2" stroke="rgba(0,0,0,0.45)" stroke-width="1.8" />
            <line v-bind="stretchWidthArrow.tick1" stroke="rgba(0,0,0,0.45)" stroke-width="1.8" />
            <line v-bind="stretchWidthArrow.tick2" stroke="rgba(0,0,0,0.45)" stroke-width="1.8" />
            <line v-bind="stretchWidthArrow.tick3" stroke="rgba(0,0,0,0.45)" stroke-width="1.8" />
            <line v-bind="stretchWidthArrow.tick4" stroke="rgba(0,0,0,0.45)" stroke-width="1.8" />

            <line :x1="stretchHeightArrow.x1" :y1="stretchHeightArrow.y1" :x2="stretchHeightArrow.x2" :y2="stretchHeightArrow.y2" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
            <line v-bind="stretchHeightArrow.tick1" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
            <line v-bind="stretchHeightArrow.tick2" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
            <line v-bind="stretchHeightArrow.tick3" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
            <line v-bind="stretchHeightArrow.tick4" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />

            <line :x1="stretchDepthArrow.x1" :y1="stretchDepthArrow.y1" :x2="stretchDepthArrow.x2" :y2="stretchDepthArrow.y2" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
            <line v-bind="stretchDepthArrow.tick1" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
            <line v-bind="stretchDepthArrow.tick2" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
            <line v-bind="stretchDepthArrow.tick3" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
            <line v-bind="stretchDepthArrow.tick4" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          </svg>

          <div class="absolute text-[13px] text-gray-500 font-semibold whitespace-nowrap -translate-x-1/2 -translate-y-1/2" :style="{ left: stretchWidthArrow.labelPos.x + 'px', top: stretchWidthArrow.labelPos.y + 'px' }">{{ stretchWidthLabel }}</div>
          <div class="absolute text-[13px] text-gray-500 font-semibold whitespace-nowrap -translate-y-1/2" :style="{ left: (stretchHeightArrow.mid.x + 8) + 'px', top: stretchHeightArrow.mid.y + 'px' }">{{ stretchHeightLabel }}</div>
          <div class="absolute text-[13px] text-gray-500 font-semibold whitespace-nowrap -translate-x-1/2 -translate-y-1/2" :style="{ left: stretchDepthArrow.labelPos.x + 'px', top: stretchDepthArrow.labelPos.y + 'px' }">{{ stretchDepthLabel }}</div>
        </div>
      </div>

      <!-- FLOATER FRAME. One wrapper the size of the frame, so the dimension callouts (drawn once, after
           the tray) are positioned exactly as they are for a normal frame and sit OUTSIDE the tray's
           drop-shadow filter and 3D transform. Inside it the tray is either the flat drawing used while
           the PNG is captured, or the live 3D tray. -->
      <div v-else-if="isFloater" class="relative shrink-0" :style="{ width: outerWpx + 'px', height: outerHpx + 'px' }">
      <template v-if="isDownloading">
      <!-- PNG CAPTURE ONLY: flat drawing of the tray (see floaterFlat). The live 3D
           tray below cannot be captured, so the download swaps to this for the moment it runs. -->
      <div
        class="shrink-0"
        :style="{
          filter: 'drop-shadow(1px 2px 2px rgba(0,0,0,.10)) drop-shadow(6px 12px 18px rgba(0,0,0,.16)) drop-shadow(16px 26px 38px rgba(0,0,0,.10))',
        }"
      >
        <div :style="{ position: 'relative', width: outerWpx + 'px', height: outerHpx + 'px' }">

          <!-- Frame face FIRST, under everything else. The capture paints a border-image's centre as
               well as its ring (the on-screen render does not), which would cover the whole tray if
               the face were drawn last. Everything else here sits inside the opening, so drawing it
               first leaves the ring visible and hides that centre. -->
          <div class="absolute inset-0" :style="{
            boxSizing: 'border-box',
            borderStyle: 'solid',
            borderWidth: framePx + 'px',
            borderImageSource: `url(${frameImage})`,
            borderImageSlice: '64',
            borderImageRepeat: 'repeat',
          }"></div>

          <!-- base fill: whatever shows through a seam is tray wood, never the page -->
          <div class="absolute" :style="{
            ...floaterTimber,
            left: floaterOpening.left + 'px', top: floaterOpening.top + 'px',
            width: floaterOpening.width + 'px', height: floaterOpening.height + 'px',
          }"><div class="absolute inset-0" style="background:rgba(0,0,0,.58);"></div></div>

          <!-- floor, scaled to where the back of the tray sits, with the work's cast shadow on it -->
          <div class="absolute" :style="{
            ...floaterTimber,
            left: floaterOpening.left + 'px', top: floaterOpening.top + 'px',
            width: floaterOpening.width + 'px', height: floaterOpening.height + 'px',
            transformOrigin: floaterFlat.floorOrigin, transform: `scale(${floaterFlat.sFloor})`,
          }">
            <div class="absolute inset-0" style="background:radial-gradient(140% 120% at 15% 12%, rgba(0,0,0,.04) 0%, rgba(0,0,0,.18) 55%, rgba(0,0,0,.40) 100%);"></div>
            <div class="absolute" :style="{
              left: (gapPx + Math.round(floaterDepthPx * 0.08)) + 'px',
              top: (gapPx + Math.round(floaterDepthPx * 0.16)) + 'px',
              width: artWpx + 'px', height: artHpx + 'px',
              boxShadow: `0 0 ${Math.round(floaterDepthPx * 0.30)}px ${Math.round(floaterDepthPx * 0.02)}px rgba(18,9,3,.30),`
                + ` ${Math.round(floaterDepthPx * 0.10)}px ${Math.round(floaterDepthPx * 0.22)}px ${Math.round(floaterDepthPx * 0.38)}px ${Math.round(-floaterDepthPx * 0.12)}px rgba(18,9,3,.55),`
                + ` ${Math.round(floaterDepthPx * 0.30)}px ${Math.round(floaterDepthPx * 0.55)}px ${Math.round(floaterDepthPx * 1.35)}px ${Math.round(floaterDepthPx * 0.10)}px rgba(18,9,3,.26)`,
            }"></div>
          </div>

          <!-- the four walls: trapezoids between the opening and the floor, same shading as the 3D walls -->
          <div class="absolute" :style="{
            ...floaterTimber,
            left: floaterOpening.left + 'px', top: floaterOpening.top + 'px',
            width: floaterOpening.width + 'px', height: floaterOpening.height + 'px',
            clipPath: floaterFlat.left,
          }"><div class="absolute inset-0" :style="{ background: `linear-gradient(90deg, rgba(0,0,0,.38) 0px, rgba(0,0,0,.62) ${floaterFlat.fL}px)` }"></div></div>
          <div class="absolute" :style="{
            ...floaterTimber,
            left: floaterOpening.left + 'px', top: floaterOpening.top + 'px',
            width: floaterOpening.width + 'px', height: floaterOpening.height + 'px',
            clipPath: floaterFlat.right,
          }"><div class="absolute inset-0" :style="{ background: `linear-gradient(270deg, rgba(0,0,0,.48) 0px, rgba(0,0,0,.68) ${floaterOpening.width - floaterFlat.fR}px)` }"></div></div>
          <div class="absolute" :style="{
            ...floaterTimber,
            left: floaterOpening.left + 'px', top: floaterOpening.top + 'px',
            width: floaterOpening.width + 'px', height: floaterOpening.height + 'px',
            clipPath: floaterFlat.top,
          }"><div class="absolute inset-0" :style="{ background: `linear-gradient(180deg, rgba(0,0,0,.45) 0px, rgba(0,0,0,.64) ${floaterFlat.fT}px)` }"></div></div>
          <div class="absolute" :style="{
            ...floaterTimber,
            left: floaterOpening.left + 'px', top: floaterOpening.top + 'px',
            width: floaterOpening.width + 'px', height: floaterOpening.height + 'px',
            clipPath: floaterFlat.bottom,
          }"><div class="absolute inset-0" :style="{ background: `linear-gradient(0deg, rgba(255,246,232,.14) 0px, rgba(0,0,0,.46) ${floaterOpening.height - floaterFlat.fB}px)` }"></div></div>

          <!-- the work, scaled to the depth it hangs at -->
          <div class="absolute overflow-hidden bg-[#2a1a10]" :style="{
            left: artLeftPx + 'px', top: artTopPx + 'px',
            width: artWpx + 'px', height: artHpx + 'px',
            transformOrigin: floaterFlat.workOrigin, transform: `scale(${floaterFlat.sWork})`,
          }">
            <img v-if="artworkImageSrc" :src="artworkImage" class="w-full h-full object-cover block" />
            <div class="absolute inset-0 pointer-events-none" :style="{
              boxShadow: `inset 0 ${Math.max(2, Math.round(gapPx * 0.30))}px ${Math.max(4, Math.round(gapPx * 0.6))}px rgba(0,0,0,.18)`
            }"></div>
          </div>

          <!-- the arris at the opening -->
          <div class="absolute pointer-events-none" :style="{
            left: floaterOpening.left + 'px', top: floaterOpening.top + 'px',
            width: floaterOpening.width + 'px', height: floaterOpening.height + 'px',
            boxShadow: '0 0 1px 0.5px rgba(0,0,0,.30)',
          }"></div>
        </div>
      </div>

      </template>
      <template v-else>
      <!-- FLOATER FRAME — a real 3D tray you look into, turned slightly (see FLOATER_YAW/PITCH
           above) so the walls read as genuine depth rather than a flat ring. The work hangs
           inside it with air all round, below the frame's face, and every surface here is the
           one timber. -->
      <div
        class="shrink-0"
        :style="{
          transform: `translate(${floaterOffsetX}px, ${floaterOffsetY}px)`,
          filter: 'drop-shadow(1px 2px 2px rgba(0,0,0,.10)) drop-shadow(6px 12px 18px rgba(0,0,0,.16)) drop-shadow(16px 26px 38px rgba(0,0,0,.10))',
          transition: TR
        }"
      >
        <div :style="{ perspective: floaterPerspectivePx + 'px', perspectiveOrigin: '50% 50%' }">
          <div :style="{
            position: 'relative', width: outerWpx + 'px', height: outerHpx + 'px',
            transformStyle: 'preserve-3d',
            transform: `rotateX(${floaterRotateXDeg}deg) rotateY(${floaterRotateYDeg}deg)`,
            transition: TR
          }">

            <!-- SEAM BACKING. Where two tray surfaces meet at a sub-pixel boundary (the mitre diagonal
                 between neighbouring walls, the work's own edge against the walls) each surface is
                 anti-aliased on its own, so the page background leaks through as a thin pale hairline
                 — it comes and goes with the frame width / floater gap because it depends on where the
                 edges land on the pixel grid. This plane sits behind everything and is scaled up by
                 exactly the perspective shrink of its depth, so on screen it fills the whole opening:
                 any leak now shows the tray's own wall tone instead of the page. Same timber and the
                 average wall shade, so it can't read as a line of its own. -->
            <div class="absolute" :style="{
              ...floaterTimber,
              left: (floaterOpening.left - FLOATER_BACKING_OVERSHOOT) + 'px', top: (floaterOpening.top - FLOATER_BACKING_OVERSHOOT) + 'px',
              width: (floaterOpening.width + 2 * FLOATER_BACKING_OVERSHOOT) + 'px', height: (floaterOpening.height + 2 * FLOATER_BACKING_OVERSHOOT) + 'px',
              transformOrigin: 'center center',
              transform: `translateZ(${-(floaterDepthPx + 1)}px) scale(${(floaterPerspectivePx + floaterDepthPx + 1) / floaterPerspectivePx})`,
              transition: TR
            }"><div class="absolute inset-0" style="background:rgba(0,0,0,.58);"></div></div>

            <!-- TRAY FLOOR, at the back. Same timber, barely shaded: the gap between the work and
                 the frame must not read as a band of some other colour. -->
            <div class="absolute" :style="{
              ...floaterTimber,
              left: (floaterOpening.left - FLOATER_FLOOR_GROW) + 'px', top: (floaterOpening.top - FLOATER_FLOOR_GROW) + 'px',
              width: (floaterOpening.width + 2 * FLOATER_FLOOR_GROW) + 'px', height: (floaterOpening.height + 2 * FLOATER_FLOOR_GROW) + 'px',
              transform: `translateZ(${-floaterDepthPx}px)`,
              transition: TR
            }">
              <!-- Ambient falloff across the floor. A flat rgba() tint here washed the whole tray
                   the same shade everywhere, which read as flat rather than as a box with depth.
                   This is a gradient instead, off-centre toward the corner furthest from the light
                   (the file's light-from-upper-left convention — see the wall shadow note below),
                   so the floor is brightest near-left and darkens gradually toward far-right. -->
              <div class="absolute inset-0" style="background:radial-gradient(140% 120% at 15% 12%, rgba(0,0,0,.04) 0%, rgba(0,0,0,.18) 55%, rgba(0,0,0,.40) 100%);"></div>

              <!-- THE WORK'S CAST SHADOW. It belongs here, on the floor, because the work is hung
                   clear of it — that suspension is the only reason a floater has a shadow at all.
                   Drawn as a child of the floor so it lives in the floor's plane and recedes with
                   it; put on the work itself it renders in the WORK's plane, which reads as a
                   sticker halo stuck to the frame rather than a shadow thrown onto something behind.
                   Three layers, not one: a faint, tight, UNOFFSET halo that hugs all four sides
                   evenly (without it, the top/left edges — which the offset layers below barely
                   reach — sit on bare floor with no shadow contact at all, and the piece reads as
                   glued flat there rather than floating), plus a near contact shadow and a broader
                   soft penumbra both pushed toward the bottom-right. A single rectangle-shaped
                   fill + one blur (the old approach) reads as a uniform halo the same width on all
                   four sides; splitting it into differently-offset layers is what makes it fall
                   away unevenly, the way a real cast shadow does, while the even halo keeps every
                   side at least somewhat "lifted". No background fill on this box — only
                   box-shadows — so there's no hard-edged silhouette sitting under the soft blur. -->
              <div class="absolute" :style="{
                left: (gapPx + FLOATER_FLOOR_GROW + Math.round(floaterDepthPx * 0.08)) + 'px',
                top: (gapPx + FLOATER_FLOOR_GROW + Math.round(floaterDepthPx * 0.16)) + 'px',
                width: artWpx + 'px', height: artHpx + 'px',
                boxShadow: `0 0 ${Math.round(floaterDepthPx * 0.30)}px ${Math.round(floaterDepthPx * 0.02)}px rgba(18,9,3,.30),`
                  + ` ${Math.round(floaterDepthPx * 0.10)}px ${Math.round(floaterDepthPx * 0.22)}px ${Math.round(floaterDepthPx * 0.38)}px ${Math.round(-floaterDepthPx * 0.12)}px rgba(18,9,3,.55),`
                  + ` ${Math.round(floaterDepthPx * 0.30)}px ${Math.round(floaterDepthPx * 0.55)}px ${Math.round(floaterDepthPx * 1.35)}px ${Math.round(floaterDepthPx * 0.10)}px rgba(18,9,3,.26)`,
                transition: TR
              }"></div>
            </div>

            <!-- THE FOUR TRAY WALLS. Each is hinged ON the opening's edge and swung back to the
                 floor, so they converge under the perspective the way the real ones do. The
                 lighting is what sells them: the top wall faces down and is darkest, the bottom
                 faces up and catches the bounce.
                 The overlay's MINIMUM opacity (its stop nearest the opening) has to be high
                 enough on its own to darken the wall to a believable wood-in-shadow tone, because
                 these walls share the master frame-face texture (see floaterTimber below), and
                 that texture carries its own baked-in highlight band. A semi-transparent black
                 overlay over a bright pixel doesn't get dark the way it looks like it should —
                 34% black over white still leaves ~66% of the brightness (~168/255, light grey),
                 which is exactly the pale streak that got flagged down one wall. Alpha-blending
                 can only ever REMOVE at most (1 - min-opacity) of the base brightness, so the
                 floor here is deliberately high on every wall, not just the one that got caught. -->
            <div class="absolute" :style="{
              ...floaterTimber,
              left: (floaterOpening.left - FLOATER_SEAM_OVERLAP) + 'px', top: (floaterOpening.top - FLOATER_SEAM_OVERLAP) + 'px',
              width: floaterDepthPx + 'px', height: (floaterOpening.height + 2 * FLOATER_SEAM_OVERLAP) + 'px',
              transformOrigin: 'left center', transform: 'translateZ(-1px) rotateY(90deg)', transition: TR
            }"><div class="absolute inset-0" style="background:linear-gradient(90deg, rgba(0,0,0,.38), rgba(0,0,0,.62));"></div></div>

            <div class="absolute" :style="{
              ...floaterTimber,
              left: (floaterOpening.left + floaterOpening.width + FLOATER_SEAM_OVERLAP) + 'px', top: (floaterOpening.top - FLOATER_SEAM_OVERLAP) + 'px',
              width: floaterDepthPx + 'px', height: (floaterOpening.height + 2 * FLOATER_SEAM_OVERLAP) + 'px',
              transformOrigin: 'left center', transform: 'translateZ(-1px) rotateY(90deg)', transition: TR
            }"><div class="absolute inset-0" style="background:linear-gradient(90deg, rgba(0,0,0,.48), rgba(0,0,0,.68));"></div></div>

            <div class="absolute" :style="{
              ...floaterTimber,
              left: (floaterOpening.left - FLOATER_SEAM_OVERLAP) + 'px', top: (floaterOpening.top - FLOATER_SEAM_OVERLAP) + 'px',
              width: (floaterOpening.width + 2 * FLOATER_SEAM_OVERLAP) + 'px', height: floaterDepthPx + 'px',
              transformOrigin: 'center top', transform: 'translateZ(-1px) rotateX(-90deg)', transition: TR
            }"><div class="absolute inset-0" style="background:linear-gradient(180deg, rgba(0,0,0,.45), rgba(0,0,0,.64));"></div></div>

            <div class="absolute" :style="{
              ...floaterTimber,
              left: (floaterOpening.left - FLOATER_SEAM_OVERLAP) + 'px', top: (floaterOpening.top + floaterOpening.height + FLOATER_SEAM_OVERLAP) + 'px',
              width: (floaterOpening.width + 2 * FLOATER_SEAM_OVERLAP) + 'px', height: floaterDepthPx + 'px',
              transformOrigin: 'center top', transform: 'translateZ(-1px) rotateX(-90deg)', transition: TR
            }"><div class="absolute inset-0" style="background:linear-gradient(180deg, rgba(255,246,232,.14), rgba(0,0,0,.46));"></div></div>

            <!-- THE WORK, hanging in the tray clear of the floor. It carries no shadow of its own:
                 its shadow is on the floor above, where it is actually thrown. No outline either —
                 a 1px box-shadow ring here drew a flat, uniform border round the whole piece on
                 every side regardless of angle, which is exactly the "sticker cut-out" look the
                 client flagged. The floor's cast shadow and the frame's own arris are what should
                 separate the work from its surroundings, not a drawn line. bg is a dark neutral
                 (not white) purely as an img-not-loaded fallback, so it can never itself show as a
                 pale sliver at the edge. -->
            <div class="absolute overflow-hidden bg-[#2a1a10]" :style="{
              left: artLeftPx + 'px', top: artTopPx + 'px',
              width: artWpx + 'px', height: artHpx + 'px',
              transform: `translateZ(${-floaterSetbackPx}px)`,
              transition: TR
            }">
              <img v-if="artworkImageSrc" :src="artworkImage" class="w-full h-full object-cover block" />
              <!-- the frame overhangs the work, so it just catches its top edge -->
              <div class="absolute inset-0 pointer-events-none" :style="{
                boxShadow: `inset 0 ${Math.max(2, Math.round(gapPx * 0.30))}px ${Math.max(4, Math.round(gapPx * 0.6))}px rgba(0,0,0,.18)`
              }"></div>
            </div>

            <!-- FRAME FACE at the very front. Its centre is open — border-image paints only the
                 border — so you look straight through it into the tray. -->
            <div class="absolute inset-0" :style="{
              boxSizing: 'border-box',
              borderStyle: 'solid',
              borderWidth: framePx + 'px',
              borderImageSource: `url(${frameImage})`,
              borderImageSlice: '64',
              borderImageRepeat: 'repeat',
              transition: TR
            }"></div>
            <!-- (There used to be a white/black bevel-highlight overlay on the frame face here —
                 inset white hairlines on its top/left, dark ones on its bottom/right. Any white
                 hairline on a wood moulding is exactly the "printed border" look the client keeps
                 flagging, so it's gone; the border-image texture already carries its own lit
                 bevel, it doesn't need a second one drawn on top in flat colour.) -->

            <!-- The arris at the opening: where the frame's face breaks away into the tray. It is
                 the sharpest edge on the whole frame in the reference photographs, and the cue
                 that most makes the eye read a lip you are looking over rather than a printed
                 border. Drawn at the face's own depth, on top of it. Softened with a touch of
                 blur rather than a hard 0-blur ring, and with no white companion line: a crisp
                 white hairline next to a crisp black one is what read as a printed border rather
                 than a real seam in timber. -->
            <div class="absolute pointer-events-none" :style="{
              left: floaterOpening.left + 'px', top: floaterOpening.top + 'px',
              width: floaterOpening.width + 'px', height: floaterOpening.height + 'px',
              boxShadow: '0 0 1px 0.5px rgba(0,0,0,.30)',
              transition: TR
            }"></div>
          </div>
        </div>
      </div>
      </template>

        <!-- DIMENSION CALLOUTS — same overall width (top) and height (right) a normal frame shows,
             covering the artwork plus the floater gap and the frame (widthLabel / heightLabel already
             include floaterW). -->
        <div class="absolute text-[13px] text-gray-500 font-semibold whitespace-nowrap" :style="{ left: 0, top: '-32px', width: outerWpx + 'px', textAlign: 'center' }">{{ widthLabel }}</div>
        <svg :width="outerWpx" height="14" class="absolute pointer-events-none" :style="{ left: '0', top: '-18px' }">
          <line x1="3" y1="7" :x2="outerWpx - 3" y2="7" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="3" y1="7" x2="8" y2="3" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="3" y1="7" x2="8" y2="11" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line :x1="outerWpx - 3" y1="7" :x2="outerWpx - 8" y2="3" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line :x1="outerWpx - 3" y1="7" :x2="outerWpx - 8" y2="11" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
        </svg>

        <svg width="14" :height="outerHpx" class="absolute pointer-events-none" :style="{ left: (outerWpx + 6) + 'px', top: '0' }">
          <line x1="7" y1="3" x2="7" :y2="outerHpx - 3" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="7" y1="3" x2="3" y2="8" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="7" y1="3" x2="11" y2="8" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="7" :y1="outerHpx - 3" x2="3" :y2="outerHpx - 8" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="7" :y1="outerHpx - 3" x2="11" :y2="outerHpx - 8" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
        </svg>
        <div class="absolute text-[13px] text-gray-500 font-semibold whitespace-nowrap" :style="{ left: (outerWpx + 24) + 'px', top: '50%', transform: 'translateY(-50%)' }">{{ heightLabel }}</div>
      </div>

      <!-- Wall shadow. Two stacked drop-shadows rather than one: a tight contact shadow that keeps
           the frame's edge crisp against the wall, plus a broad ambient one for the soft falloff.
           A single wide shadow makes the frame look like it is hovering well off the wall. Offset
           down and to the RIGHT to agree with the light direction already baked into the textures
           (the left wall is lit at 94% and the right at 43%, i.e. light comes from upper-left). -->
      <div v-else class="relative" :style="{
        width: outerWpx + 'px', height: outerHpx + 'px',
        filter: 'drop-shadow(2px 3px 4px rgba(0,0,0,0.14)) drop-shadow(6px 14px 26px rgba(0,0,0,0.20))',
        transition: TR,
        // iOS Safari has a known bug where an element with `filter` doesn't get re-composited when
        // its CHILDREN change (a mount/colour/material swap below) — the shadow and everything
        // inside it just sits there stale until a scroll or tap forces WebKit to redo compositing,
        // which is exactly 'change a dropdown -> nothing updates -> scroll -> it snaps correct'.
        // Promoting this element to its own GPU layer up front (a translateZ(0) that moves
        // nothing) keeps WebKit compositing it properly every time instead of only when poked.
        // Not applied during the iOS PNG capture only: WebKit paints GPU-layered content blank inside
        // the SVG foreignObject the export is drawn through.
        WebkitTransform: iosCapturing ? 'none' : 'translateZ(0)',
        transform: iosCapturing ? 'none' : 'translateZ(0)',
        WebkitBackfaceVisibility: iosCapturing ? 'visible' : 'hidden',
      }">

        <!-- iOS capture only: the frame ring pre-drawn on a canvas, as a plain <img>. WebKit does not
             wait for a CSS border-image inside the exported SVG, so the frame came out empty. -->
        <img v-if="iosFrameRing" :src="iosFrameRing" class="absolute inset-0 w-full h-full block" />

        <!-- FRAME: 9-slice border-image so the grain runs along each member's length and the
             corners meet in real 45deg mitres, the way an actual moulding is cut. border-image
             maps the corner/edge slices itself, so there are no stacked clipped layers.
             The slice is 64 (not 128) on purpose: the master texture has a chamfer ramp baked in
             that darkens ~33% from the outer edge to the middle, and pulling 128px of it into the
             border made the face read as a deep bevel. The reference moulding is a flat
             square-edge face, and the outer 64px band is the flat part -- bright outer lip, then
             an even face. The mitre diagonal still runs corner-to-corner of a 64px corner slice,
             so the joint is unaffected. -->
        <div v-if="!iosFrameRing" class="absolute inset-0" :style="{
          boxSizing: 'border-box',
          borderStyle: 'solid',
          borderWidth: framePx + 'px',
          borderImageSource: `url(${frameImage})`,
          borderImageSlice: '64',
          borderImageRepeat: 'repeat',
          transition: TR
        }"></div>

        <!-- NOTE: a soft 150deg lighting gradient used to sit here. The face texture now carries
             per-side lighting measured off the real frame (top 100% / left 94% / right 97% /
             bottom 88%), and a corner-to-corner gradient on top of that fights it — it re-lit the
             bottom-right of the frame and flattened the very contrast the recess depends on. -->


        <!-- FLOATER RECESS (canvas only): the frame box's inner walls, i.e. the same moulding seen
             edge-on. The per-side brightness is MEASURED off the client's real frame photo
             (website/floater frame.png): top wall 25% of the face (it faces down, deep shadow),
             right 43%, left 51%, bottom ~100% (faces up, fully lit). That SPREAD is what makes the
             recess read as a box you can see into — a previous pass flattened all four to a uniform
             80% and it immediately stopped reading as recessed, so don't even them out.
             The remaining depth cue is the crisp dark arris on the outside of this box, where the
             face and the wall meet.
             Slice is 64 here too: the wall is only ~1in wide, so at any realistic scale it is a
             thin band, and squeezing 128px of ramp into it collapsed into a dark smudge that read
             as a thin dark line rather than a wall (the client flagged exactly that). The outer
             64px band is the light, even part, so the wall now reads as a lit plane and the depth
             comes from the dark arris at the face junction plus the artwork's contact shadow --
             which is how the reference photo actually reads. -->
        <!-- MOUNT layer -->
        <div v-if="mountPx > 0.5" class="absolute" :style="{
          left: (framePx + gapPx) + 'px', top: (framePx + gapPx) + 'px',
          width: (outerWpx - 2 * framePx - 2 * gapPx) + 'px', height: (outerHpx - 2 * framePx - 2 * gapPx) + 'px',
          background: '#f6f4ee',
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.06), inset 0 1px 3px rgba(0,0,0,.12)',
          transition: TR
        }"></div>

        <!-- ARTWORK: box shaped to the photo's own aspect ratio, so nothing is cropped or gapped -->
        <div class="absolute overflow-hidden bg-white" :style="{
          left: artLeftPx + 'px', top: artTopPx + 'px',
          width: artWpx + 'px', height: artHpx + 'px',
          boxShadow: isCanvas
            ? (contactX + 'px ' + contactY + 'px ' + contactBlur + 'px rgba(62,27,0,.80)')
            : 'inset 0 0 0 1px rgba(0,0,0,0.15)',
          transition: TR
        }">
          <img v-if="artworkImageSrc" :src="artworkImage" class="w-full h-full object-cover block" />

          <!-- GLASS sheen overlay -->
          <div v-if="!isCanvas && wantGlass" class="absolute inset-0 pointer-events-none" style="background:linear-gradient(120deg, rgba(255,255,255,.35) 0%, rgba(255,255,255,.08) 18%, rgba(255,255,255,0) 32%, rgba(255,255,255,0) 68%, rgba(255,255,255,.10) 82%, rgba(255,255,255,.22) 100%);"></div>
        </div>

        <!-- NOTE: there used to be a "floater cast shadow" overlay here, darkening the top and left
             edges of the artwork on the theory that the frame throws onto the recessed canvas.
             Measuring the client's real frame photo disproves it: the canvas face is uniformly lit
             right to its edges (L=237 at the top edge vs 233 mid-canvas — very slightly BRIGHTER,
             not darker). The depth cue there is entirely the dark top wall, not shading on the art.
             Don't reintroduce it; it only muddied the artwork. -->

        <!-- SIDE DEPTH: the spacer sets the artwork back behind the glass, so it shows as the
             shadow the frame casts onto the mount (strongest along the top, then the left, as in
             the reference render) rather than as a band that pushes the artwork inward. Sits above
             the mount/artwork so the shadow falls across them. -->
        <div v-if="hasSideDepth" class="absolute pointer-events-none" :style="{
          left: framePx + 'px', top: framePx + 'px',
          width: (outerWpx - 2 * framePx) + 'px', height: (outerHpx - 2 * framePx) + 'px',
          boxShadow: 'inset 0 ' + depthPx + 'px ' + (depthPx * 1.9) + 'px ' + (-depthPx * 0.25) + 'px rgba(0,0,0,.5),'
            + ' inset ' + (depthPx * 0.75) + 'px 0 ' + (depthPx * 1.6) + 'px ' + (-depthPx * 0.35) + 'px rgba(0,0,0,.34),'
            + ' inset ' + (-depthPx * 0.35) + 'px 0 ' + (depthPx * 1.1) + 'px ' + (-depthPx * 0.5) + 'px rgba(0,0,0,.16),'
            + ' inset 0 ' + (-depthPx * 0.3) + 'px ' + (depthPx * 1.0) + 'px ' + (-depthPx * 0.5) + 'px rgba(0,0,0,.14)',
          transition: TR
        }"></div>

        <!-- DIMENSION CALLOUTS: the actual take-away numbers for what's being ordered — outer
             width across the top, outer height down the right — covering the artwork plus
             whichever frame/mount/floater gap it's dressed in. Drawn as plain SVG chevrons
             (not <marker> arrowheads) on purpose: this component can render several times on one
             page (cart with multiple framed items), and <marker> ids are looked up document-wide,
             so duplicate marker ids across instances would resolve to whichever instance's <defs>
             happens to be first. Stretch mode is excluded — it has no frame/mount to add to the
             artwork's own size, and its box is a turned 3D object these flat overlays don't track. -->
        <div class="absolute text-[13px] text-gray-500 font-semibold whitespace-nowrap" :style="{ left: 0, top: '-32px', width: outerWpx + 'px', textAlign: 'center' }">{{ widthLabel }}</div>
        <svg :width="outerWpx" height="14" class="absolute pointer-events-none" :style="{ left: '0', top: '-18px' }">
          <line x1="3" y1="7" :x2="outerWpx - 3" y2="7" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="3" y1="7" x2="8" y2="3" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="3" y1="7" x2="8" y2="11" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line :x1="outerWpx - 3" y1="7" :x2="outerWpx - 8" y2="3" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line :x1="outerWpx - 3" y1="7" :x2="outerWpx - 8" y2="11" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
        </svg>

        <svg width="14" :height="outerHpx" class="absolute pointer-events-none" :style="{ left: (outerWpx + 6) + 'px', top: '0' }">
          <line x1="7" y1="3" x2="7" :y2="outerHpx - 3" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="7" y1="3" x2="3" y2="8" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="7" y1="3" x2="11" y2="8" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="7" :y1="outerHpx - 3" x2="3" :y2="outerHpx - 8" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
          <line x1="7" :y1="outerHpx - 3" x2="11" :y2="outerHpx - 8" stroke="rgba(0,0,0,0.45)" stroke-width="1.2" />
        </svg>
        <div class="absolute text-[13px] text-gray-500 font-semibold whitespace-nowrap" :style="{ left: (outerWpx + 24) + 'px', top: '50%', transform: 'translateY(-50%)' }">{{ heightLabel }}</div>

      </div>
    </div>
  </div>
</template>

<style scoped>
/* The div layers get their transition inline via TR. The mitre seams are SVG, and x1/y1/x2/y2 and
   x/y/width/height are SVG2 geometry PROPERTIES, so they animate from CSS in Chrome/Firefox/Edge
   and simply snap elsewhere — which is exactly the old behaviour, so this degrades safely.
   Keep this duration in step with TR in the script below. */
.frame-seams line,
.frame-seams rect {
  transition: all 320ms cubic-bezier(0.4, 0, 0.2, 1);
}
</style>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { useElementSize } from '@vueuse/core';
import { useMeasurementUnit } from '~/composables/useMeasurementUnit';
import { stretcherBarDepthInches } from '~/utils/framingCalculator';
import naturalLacquerFrame from '~/assets/textures/frame/natural-lacquer-face.jpg';
import woodenPolishedFrame from '~/assets/textures/frame/wooden-polished-face.jpg';
import paintedWhiteFrame from '~/assets/textures/frame/painted-white-face.jpg';
import paintedBlackFrame from '~/assets/textures/frame/painted-black-face.jpg';
import aluminiumFrame from '~/assets/textures/frame/aluminium-face.jpg';
import naturalLacquerRecess from '~/assets/textures/frame/natural-lacquer-recess.jpg';
import woodenPolishedRecess from '~/assets/textures/frame/wooden-polished-recess.jpg';
import paintedWhiteRecess from '~/assets/textures/frame/painted-white-recess.jpg';
import paintedBlackRecess from '~/assets/textures/frame/painted-black-recess.jpg';
import aluminiumRecess from '~/assets/textures/frame/aluminium-recess.jpg';

const props = defineProps<{
  specifications: Record<string, any> | null | undefined;
  artworkLength: number;
  artworkHeight: number;
  artworkImageSrc?: string;
  photoWidth?: number;
  photoHeight?: number;
  // 'stretch' draws the canvas on its bars with no frame, turned slightly so the returning
  // edge — the depth the bars give it — is visible, the way a stretched canvas is photographed.
  mode?: 'frame' | 'stretch';
  // Checkout is genuinely live (it redraws as the buyer edits the calculator), but a read-only
  // render of a past order's already-finished choice — e.g. the Sales Summary "eye" button — isn't
  // "live" at all, so that caller overrides the heading rather than living with the wrong word.
  heading?: string;
  unit?: 'in' | 'cm';
}>();

const { globalUnit } = useMeasurementUnit();
const activeUnit = computed(() => props.unit ?? globalUnit.value);

// Every layer's geometry is derived from the spec values, so changing a dropdown used to snap the
// whole frame to its new size in one frame. Transitioning the layers instead lets the moulding
// visibly grow or the recess visibly deepen, which reads as the frame being adjusted.
// `all` is deliberate: each layer animates a different mix of width/height/left/top/border-width/
// box-shadow, and listing them per layer would be four different lists to keep in sync. The
// non-animatable properties in these style objects (border-image-source, border-style) are simply
// ignored — notably the texture swap when the material changes still cuts instantly, which is
// correct, since cross-fading one wood into another looks like a glitch rather than a change.
//
// 'none' until hasSettled (see containerRef/useElementSize below) — before the container's REAL
// size has been measured, every layer is sized off availableW/availableH's fallback numbers, and
// that first render-with-real-measurements that follows is a genuine style CHANGE, which this
// transition would otherwise animate exactly like a deliberate dropdown edit. On a device whose
// first ResizeObserver callback lands quickly that 320ms grow-into-place is barely visible; on
// one where it lands slower (iOS Safari reports its first callback later than Chrome does), the
// same animation reads as "nothing shows, then it suddenly appears" — the preview isn't actually
// broken, it's animating from a wrong starting size that was never meant to be seen.
const TR = computed(() => (hasSettled.value ? 'all 320ms cubic-bezier(0.4, 0, 0.2, 1)' : 'none'));

const specs = computed(() => props.specifications || {});

const isStretchMode = computed(() => props.mode === 'stretch');
const isCanvas = computed(() => specs.value.artworkType === 'Canvas');
const wantGlass = computed(() => !!specs.value.glassType);

const artW = computed(() => props.artworkLength || 12);
const artH = computed(() => props.artworkHeight || 16);

const frameW = computed(() => specs.value.frameFront || 0);
const mountW = computed(() => (specs.value.wantMount && !isCanvas.value) ? (specs.value.mountWidth || 0) : 0);
const floaterW = computed(() => (specs.value.frameType === 'Floater Frame' && isCanvas.value) ? (specs.value.floaterSpace || 0) : 0);
// Side depth is a spacer that holds the glass off the artwork — it adds depth behind the glass,
// not width to the opening. So it must not displace the artwork inward; it reads instead as the
// shadow the frame casts down onto the mount (see the side-depth overlay in the template).
const hasSideDepth = computed(() => !!specs.value.wantSideDepth && !isCanvas.value);

// Real artwork photos usually carry a sliver of extra background/mat beyond the declared
// physical size, so their pixel aspect ratio rarely matches artW/artH exactly. The artwork's
// own box is shaped to the photo's real aspect ratio (instead of the declared-inches ratio) so
// the full photo always fits with no crop and no letterbox gap; the outer frame/mount box is
// then sized AROUND that adjusted artwork box (not the other way around), keeping the border
// thickness symmetric and physically accurate on every side.
const photoAspect = computed(() => (props.photoWidth && props.photoHeight) ? props.photoWidth / props.photoHeight : null);
// The height the artwork actually occupies once shaped to the photo, in inches — the rendered
// outer size is measured from this (not artH) so the fit check below can never be off.
const artHFit = computed(() => photoAspect.value ? artW.value / photoAspect.value : artH.value);

const openingW = computed(() => artW.value + 2 * mountW.value + 2 * floaterW.value);
const openingH = computed(() => artHFit.value + 2 * mountW.value + 2 * floaterW.value);

// How far a stretched canvas stands off the wall — the bar's depth, e.g. "1.5x2" is 2".
const stretchDepthIn = computed(() => stretcherBarDepthInches(specs.value.stretcherBar));

// Rendered outer size in inches.
const declaredOuterW = computed(() => openingW.value + 2 * frameW.value);
const declaredOuterH = computed(() => openingH.value + 2 * frameW.value);

// Dimension callout text — one decimal place, trimmed when it's a whole number.
function fmtDimension(v: number): string {
  if (activeUnit.value === 'cm') {
    const cm = Math.round(v * 2.54 * 10) / 10;
    return (Number.isInteger(cm) ? cm.toFixed(0) : cm.toFixed(1)) + ' cm';
  }
  const rounded = Math.round(v * 10) / 10;
  return (Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)) + '"';
}
// The callouts must read the artwork's DECLARED size (what's on the order/label), not artHFit —
// artHFit is the photo-aspect-adjusted stand-in used purely so the rendered box never crops or
// gaps the photo, and it drifts from the declared height whenever the photo's own pixel aspect
// ratio isn't an exact match (which is almost always). declaredOuterW already used raw artW, so
// only the height side needed its own real-height version here.
const labelOuterH = computed(() => artH.value + 2 * mountW.value + 2 * floaterW.value + 2 * frameW.value);
const widthLabel = computed(() => fmtDimension(declaredOuterW.value));
const heightLabel = computed(() => fmtDimension(labelOuterH.value));
// Stretch has no frame/mount around the canvas — the callout is just the canvas's own declared size.
const stretchWidthLabel = computed(() => fmtDimension(artW.value));
const stretchHeightLabel = computed(() => fmtDimension(artH.value));

// --- STRETCHED CANVAS GEOMETRY ---
// Turn angles, in degrees.
// The yaw has to be this big: a return only projects to depth * sin(yaw), so at 14deg a 1" bar
// shows as a quarter-inch sliver that reads as a border rather than as depth; at 28deg it
// projects to ~47% of the bar, so a 1" bar and a 2" bar are plainly different objects.
// The pitch stays shallow: the camera is only slightly above the work, so the top return is a
// sliver rather than a face you look down onto.
const STRETCH_YAW = 28;
const STRETCH_PITCH = 7;
const RAD = Math.PI / 180;

// How big the artwork can be drawn so the turned box fits the panel. On screen the box is two
// parts that scale differently: the face, which shrinks with the artwork and foreshortens by
// cos(yaw), and the bar depth, which is a FIXED number of px (see stretchDepthPx) of which
// sin(yaw) shows. So the artwork gets whatever room is left once the depth has taken its share.
// The turned box is narrower than the flat artwork, which is why this is worked out from the turn
// and not from the artwork's own width — otherwise the render comes out far smaller than its space.
// The 1.06 is headroom for perspective, which this flat projection ignores: it enlarges the near
// edge by a few percent, and without the margin the widest pieces would graze the panel's edge.
const STRETCH_HEADROOM = 1.06;
const stretchFitScaleW = computed(() =>
  (availableW.value / STRETCH_HEADROOM - stretchDepthPx.value * Math.sin(STRETCH_YAW * RAD))
  / Math.max(1, artW.value * Math.cos(STRETCH_YAW * RAD)));
const stretchFitScaleH = computed(() =>
  (availableH.value / STRETCH_HEADROOM - stretchDepthPx.value * Math.sin(STRETCH_PITCH * RAD))
  / Math.max(1, artHFit.value * Math.cos(STRETCH_PITCH * RAD)));

// One inch is this many pixels at most — the ceiling stretch mode caps itself at (see
// STRETCH_MAX_PX_PER_INCH) and the stand-in scale stretchDepthPx/depthPx still use for shadow and
// bar-depth sizing. NOT a floor for the regular (non-stretch) frame/artwork scale any more — see
// uniformScale below, which deliberately has no such cap so artwork+frame always scale up to fill
// whichever of width/height is the binding constraint, per the client's explicit request to let
// the preview use the full available space rather than stay true-to-life-small.
// History, so nobody re-treads it: 13 -> 18 on 2026-09-09, because a 1" floater recess was too thin
// to see — the wall is literally 1 * PX_PER_INCH pixels wide, and at 13 most of that was eaten by
// edge rings and shadows (the client's reference photo reads at roughly 27px/inch). Back to 13 on
// 2026-09-11 at the client's request, previews being too large at 18. If the floater recess is ever
// reported as invisible again, this constant is the cause and the fix is to raise it.
const PX_PER_INCH = 13;
// Outer (border-box) height budget for containerRef — see the template's h-[300px] lg:h-[420px].
// This is a FIXED box, not a cap artwork steps down from: artwork and frame now scale TOGETHER
// (see uniformScale) to fill whichever of availableW/availableH is tighter, so both can shrink
// together as front-width/mount grow. Kept as a named constant only as availableH's fallback
// before the real measurement arrives, and it must match the template's lg: height.
const MAX_PREVIEW_H = 420;

const containerRef = ref<HTMLElement | null>(null);
const { width: containerWidth, height: containerHeight } = useElementSize(containerRef);

// Flips once, permanently, the first time BOTH dimensions have a real measurement — see TR above
// for why. Deliberately waits for both width and height rather than either alone: a dialog that's
// still being laid out can report one before the other, and starting the "it's fine to animate
// now" clock on a partial measurement would just move the same race by a few milliseconds instead
// of removing it. If the component mounts hidden (v-show, a closed-but-mounted dialog) both stay
// 0 and this simply waits — it still settles correctly the moment the dialog actually opens.
const hasSettled = ref(false);
watch([containerWidth, containerHeight], ([w, h]) => {
  if (w > 0 && h > 0) hasSettled.value = true;
}, { immediate: true });

// Saves what the buyer is looking at as a PNG. html-to-image lets the browser render the DOM
// itself (SVG foreignObject), which is what keeps the stretch view's CSS 3D intact — html2canvas
// can't do perspective/preserve-3d.
const isDownloading = ref(false);
// html-to-image serialises the DOM as XML, and XML forbids "--" inside a comment. Vue keeps this
// template's comments in dev builds and several of them contain a " -- " dash, which made the whole
// export fail with an opaque image "error" event (frame mode only; the stretch view's comments
// happen not to have one). Comments carry nothing visual, so the capture skips them (nodeType 8).
const skipComments = (node: Node) => node.nodeType !== Node.COMMENT_NODE;
// The frame moulding is a CSS border-image, which html-to-image does not inline, so in the exported
// SVG its plain URL can't load and the frame came out uncoloured. For the capture only, the texture
// is swapped for a data: URL.
const inlinedFrameImage = ref<string | null>(null);
async function toDataUrl(url: string) {
  if (!url || url.startsWith('data:')) return url;
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (!res.ok) return url;
    const blob = await res.blob();
    return await new Promise<string>((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve((r.result as string) || url);
      r.onerror = () => resolve(url);
      r.readAsDataURL(blob);
    });
  } catch {
    return url;
  }
}

async function loadImageElement(url: string): Promise<HTMLImageElement> {
  const img = new Image();
  if (url.startsWith('http://') || url.startsWith('https://')) {
    img.crossOrigin = 'anonymous';
  }
  img.src = url;
  await new Promise<void>((resolve) => {
    if (img.complete && img.naturalWidth > 0) return resolve();
    img.onload = () => resolve();
    img.onerror = () => {
      if (img.crossOrigin) {
        const fallbackImg = new Image();
        fallbackImg.onload = () => resolve();
        fallbackImg.onerror = () => resolve();
        fallbackImg.src = url;
        return;
      }
      resolve();
    };
  });
  return img;
}

async function waitForImagesToDecode(root: HTMLElement) {
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(imgs.map((img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    if (typeof img.decode === 'function') {
      return img.decode().catch(() => new Promise<void>((resolve) => {
        img.addEventListener('load', () => resolve(), { once: true });
        img.addEventListener('error', () => resolve(), { once: true });
      }));
    }
    return new Promise<void>((resolve) => {
      img.addEventListener('load', () => resolve(), { once: true });
      img.addEventListener('error', () => resolve(), { once: true });
    });
  }));
}

async function preloadImage(dataUrl: string) {
  await new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = dataUrl;
  });
}

// iPhone / iPad only (an iPad reports itself as a touch-capable Mac).
function isIosDevice() {
  if (import.meta.server) return false;
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const platform = (typeof navigator !== 'undefined' && navigator.platform) || '';
  const maxTouchPoints = (typeof navigator !== 'undefined' && navigator.maxTouchPoints) || 0;
  return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);
}

// Any WebKit: iOS devices (every browser there is WebKit) plus Safari on the Mac.
function isIOS() {
  if (import.meta.server) return false;
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const isSafariBrowser = /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|Android/.test(ua);
  return isIosDevice() || isSafariBrowser;
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const iosCapturing = ref(false);
const iosFrameRing = ref<string | null>(null);
function drawFrameRingToContext(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  w: number,
  h: number,
  bw: number,
  scale: number
) {
  const iw = img.naturalWidth, ih = img.naturalHeight, s = 64;
  if (!iw || !ih) return;
  const W = Math.round(w * scale), H = Math.round(h * scale), b = Math.round(bw * scale);
  const k = b / s;

  // corners
  ctx.drawImage(img, 0, 0, s, s, 0, 0, b, b);
  ctx.drawImage(img, iw - s, 0, s, s, W - b, 0, b, b);
  ctx.drawImage(img, 0, ih - s, s, s, 0, H - b, b, b);
  ctx.drawImage(img, iw - s, ih - s, s, s, W - b, H - b, b, b);

  // edges
  const tileW = (iw - 2 * s) * k, tileH = (ih - 2 * s) * k;
  const tileH_ = (y: number, sx: number, sy: number) => {
    const start = b, end = W - b;
    ctx.save(); ctx.beginPath(); ctx.rect(start, y, end - start, b); ctx.clip();
    let cx = (start + end) / 2 - tileW / 2;
    cx -= Math.ceil((cx - start) / tileW) * tileW;
    for (; cx < end; cx += tileW) ctx.drawImage(img, sx, sy, iw - 2 * s, s, cx, y, tileW, b);
    ctx.restore();
  };
  const tileV_ = (x: number, sx: number, sy: number) => {
    const start = b, end = H - b;
    ctx.save(); ctx.beginPath(); ctx.rect(x, start, b, end - start); ctx.clip();
    let cy = (start + end) / 2 - tileH / 2;
    cy -= Math.ceil((cy - start) / tileH) * tileH;
    for (; cy < end; cy += tileH) ctx.drawImage(img, sx, sy, s, ih - 2 * s, x, cy, b, tileH);
    ctx.restore();
  };
  tileH_(0, s, 0);            // top
  tileH_(H - b, s, ih - s);   // bottom
  tileV_(0, 0, s);            // left
  tileV_(W - b, iw - s, s);   // right
}

// ---------------------------------------------------------------------------------------------
// SAFARI / iOS EXPORT. WebKit cannot be trusted to paint this preview through html-to-image's SVG
// foreignObject (border-image, filters and CSS 3D come out missing or blurred), so for WebKit only
// the same picture is redrawn on a plain 2D canvas, from the same computed geometry the template
// uses. Every block below mirrors one template layer; if a layer changes there, change it here too.
// ---------------------------------------------------------------------------------------------
type Pt = { x: number; y: number };
type Box = { x: number; y: number; w: number; h: number };
const EXPORT_SCALE = 2; // same pixelRatio the html-to-image export uses
const LABEL_COLOR = '#6a7282'; // text-gray-500
const LABEL_LINE_H = 19.5; // a 13px callout at the inherited 1.5 line-height
const CALLOUT_STROKE = 'rgba(0,0,0,0.45)';

function tracePolygon(ctx: CanvasRenderingContext2D, pts: Pt[]) {
  pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
  ctx.closePath();
}

// A CSS shadow (drop-shadow / box-shadow) of whatever `trace` outlines, WITHOUT the shape itself:
// the shape is filled off-canvas and only its shadow lands in view. Canvas shadow offset and blur
// ignore the current transform, so they take the device scale `k` explicitly.
function castShadow(
  ctx: CanvasRenderingContext2D, trace: () => void,
  x: number, y: number, blur: number, color: string,
  k = EXPORT_SCALE, rule: CanvasFillRule = 'nonzero'
) {
  const far = ctx.canvas.width * 2 + 4096;
  const m = ctx.getTransform();
  ctx.save();
  ctx.setTransform(m.a, m.b, m.c, m.d, m.e - far, m.f);
  ctx.shadowColor = color;
  // x1.4: WebKit's canvas blur falls off sooner than its CSS one; measured against the Chrome export
  ctx.shadowBlur = blur * k * 1.4;
  ctx.shadowOffsetX = x * k + far;
  ctx.shadowOffsetY = y * k;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  trace();
  ctx.fill(rule);
  ctx.restore();
}

// box-shadow: inset x y blur spread color
function insetShadow(
  ctx: CanvasRenderingContext2D, b: Box,
  x: number, y: number, blur: number, spread: number, color: string, k = EXPORT_SCALE
) {
  const pad = Math.abs(x) + Math.abs(y) + blur * 2 + Math.abs(spread) + 4;
  ctx.save();
  ctx.beginPath();
  ctx.rect(b.x, b.y, b.w, b.h);
  ctx.clip();
  castShadow(ctx, () => {
    ctx.rect(b.x - pad, b.y - pad, b.w + 2 * pad, b.h + 2 * pad);
    const iw = b.w - 2 * spread, ih = b.h - 2 * spread;
    if (iw > 0 && ih > 0) ctx.rect(b.x + spread, b.y + spread, iw, ih);
  }, x, y, blur, color, k, 'evenodd');
  ctx.restore();
}

// box-shadow: x y blur spread color (outer). Like CSS, nothing is painted inside the box itself.
function outerShadow(
  ctx: CanvasRenderingContext2D, b: Box,
  x: number, y: number, blur: number, spread: number, color: string, k = EXPORT_SCALE
) {
  const pad = Math.abs(x) + Math.abs(y) + blur * 2 + Math.abs(spread) + 4;
  ctx.save();
  ctx.beginPath();
  ctx.rect(b.x - pad, b.y - pad, b.w + 2 * pad, b.h + 2 * pad);
  ctx.rect(b.x, b.y, b.w, b.h);
  ctx.clip('evenodd');
  castShadow(ctx, () => ctx.rect(b.x - spread, b.y - spread, b.w + 2 * spread, b.h + 2 * spread), x, y, blur, color, k);
  ctx.restore();
}

// linear-gradient(<deg>, ...stops) across a box, with CSS's own gradient-line length.
function cssLinearGradient(ctx: CanvasRenderingContext2D, b: Box, deg: number, stops: [number, string][]) {
  const dx = Math.sin(deg * RAD), dy = -Math.cos(deg * RAD);
  const half = (Math.abs(b.w * dx) + Math.abs(b.h * dy)) / 2;
  const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
  const g = ctx.createLinearGradient(cx - dx * half, cy - dy * half, cx + dx * half, cy + dy * half);
  for (const [o, c] of stops) g.addColorStop(o, c);
  return g;
}

// A two-stop gradient between two points (the "0px ... Npx" wall gradients).
function twoStopGradient(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, from: string, to: string) {
  // a zero-length canvas gradient paints nothing at all, so keep it a hair long
  if (x0 === x1 && y0 === y1) x1 += 0.01;
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  g.addColorStop(0, from);
  g.addColorStop(1, to);
  return g;
}

function fillBox(ctx: CanvasRenderingContext2D, b: Box, style: string | CanvasGradient) {
  ctx.fillStyle = style;
  ctx.fillRect(b.x, b.y, b.w, b.h);
}

// <img class="object-cover"> filling a box.
function drawImageCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement | null, b: Box) {
  const crop = coverCrop(img, b.w, b.h);
  if (!img || !crop) return;
  ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, b.x, b.y, b.w, b.h);
}
function coverCrop(img: HTMLImageElement | null, w: number, h: number): Box | null {
  const iw = img?.naturalWidth || 0, ih = img?.naturalHeight || 0;
  if (!iw || !ih || w <= 0 || h <= 0) return null;
  const s = Math.max(w / iw, h / ih);
  return { x: (iw - w / s) / 2, y: (ih - h / s) / 2, w: w / s, h: h / s };
}

function scaleAbout(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number) {
  ctx.translate(cx, cy);
  ctx.scale(s, s);
  ctx.translate(-cx, -cy);
}

function strokeSegments(ctx: CanvasRenderingContext2D, segs: [number, number, number, number][], width: number) {
  ctx.strokeStyle = CALLOUT_STROKE;
  ctx.lineWidth = width;
  ctx.beginPath();
  for (const [x1, y1, x2, y2] of segs) {
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
  }
  ctx.stroke();
}

function drawLabel(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, align: CanvasTextAlign) {
  ctx.fillStyle = LABEL_COLOR;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x, y);
}

// The width (top) and height (right) callouts shared by the classic and floater frames.
function drawFlatCallouts(ctx: CanvasRenderingContext2D, w: number, h: number) {
  const ty = -18 + 7;
  strokeSegments(ctx, [
    [3, ty, w - 3, ty],
    [3, ty, 8, ty - 4], [3, ty, 8, ty + 4],
    [w - 3, ty, w - 8, ty - 4], [w - 3, ty, w - 8, ty + 4],
  ], 1.2);
  const rx = w + 6 + 7;
  strokeSegments(ctx, [
    [rx, 3, rx, h - 3],
    [rx, 3, rx - 4, 8], [rx, 3, rx + 4, 8],
    [rx, h - 3, rx - 4, h - 8], [rx, h - 3, rx + 4, h - 8],
  ], 1.2);
  drawLabel(ctx, widthLabel.value, w / 2, -32 + LABEL_LINE_H / 2, 'center');
  drawLabel(ctx, heightLabel.value, w + 24, h / 2, 'left');
}

// CLASSIC FRAME (the template's v-else branch).
function drawClassicFrame(ctx: CanvasRenderingContext2D, frameImg: HTMLImageElement | null, artImg: HTMLImageElement | null) {
  const W = outerWpx.value, H = outerHpx.value, fp = framePx.value;
  const outer = () => ctx.rect(0, 0, W, H);
  // wall shadow
  castShadow(ctx, outer, 2, 3, 4, 'rgba(0,0,0,0.14)');
  castShadow(ctx, outer, 6, 14, 26, 'rgba(0,0,0,0.20)');

  if (frameImg) drawFrameRingToContext(ctx, frameImg, W, H, fp, 1);

  if (mountPx.value > 0.5) {
    const inset = fp + gapPx.value;
    const mount = { x: inset, y: inset, w: W - 2 * inset, h: H - 2 * inset };
    fillBox(ctx, mount, '#f6f4ee');
    insetShadow(ctx, mount, 0, 1, 3, 0, 'rgba(0,0,0,.12)');
    insetShadow(ctx, mount, 0, 0, 0, 1, 'rgba(0,0,0,.06)');
  }

  const art = { x: artLeftPx.value, y: artTopPx.value, w: artWpx.value, h: artHpx.value };
  if (isCanvas.value) outerShadow(ctx, art, contactX.value, contactY.value, contactBlur.value, 0, 'rgba(62,27,0,.80)');
  fillBox(ctx, art, '#ffffff');
  drawImageCover(ctx, artImg, art);
  if (!isCanvas.value && wantGlass.value) {
    fillBox(ctx, art, cssLinearGradient(ctx, art, 120, [
      [0, 'rgba(255,255,255,.35)'], [0.18, 'rgba(255,255,255,.08)'], [0.32, 'rgba(255,255,255,0)'],
      [0.68, 'rgba(255,255,255,0)'], [0.82, 'rgba(255,255,255,.10)'], [1, 'rgba(255,255,255,.22)'],
    ]));
  }

  if (hasSideDepth.value) {
    const d = depthPx.value;
    const side = { x: fp, y: fp, w: W - 2 * fp, h: H - 2 * fp };
    // last-listed shadow first: CSS paints the first one in the list on top
    insetShadow(ctx, side, 0, -d * 0.3, d * 1.0, -d * 0.5, 'rgba(0,0,0,.14)');
    insetShadow(ctx, side, -d * 0.35, 0, d * 1.1, -d * 0.5, 'rgba(0,0,0,.16)');
    insetShadow(ctx, side, d * 0.75, 0, d * 1.6, -d * 0.35, 'rgba(0,0,0,.34)');
    insetShadow(ctx, side, 0, d, d * 1.9, -d * 0.25, 'rgba(0,0,0,.5)');
  }

  drawFlatCallouts(ctx, W, H);
}

// FLOATER FRAME — the flat tray the template draws while a PNG is captured (see floaterFlat).
function drawFloaterFrame(ctx: CanvasRenderingContext2D, frameImg: HTMLImageElement | null, artImg: HTMLImageElement | null) {
  const W = outerWpx.value, H = outerHpx.value, fp = framePx.value;
  const o = floaterOpening.value, f = floaterFlat.value, d = floaterDepthPx.value, gap = gapPx.value;
  const opening = { x: o.left, y: o.top, w: o.width, h: o.height };
  const cx = W / 2, cy = H / 2;
  const outer = () => ctx.rect(0, 0, W, H);
  // floaterTimber: the face texture stretched to the frame's outer size, laid from the opening's corner
  const timber = () => {
    fillBox(ctx, opening, '#6d4a2f');
    if (!frameImg) return;
    ctx.save();
    ctx.beginPath();
    ctx.rect(opening.x, opening.y, opening.w, opening.h);
    ctx.clip();
    ctx.drawImage(frameImg, opening.x, opening.y, W, H);
    ctx.restore();
  };

  castShadow(ctx, outer, 1, 2, 2, 'rgba(0,0,0,.10)');
  castShadow(ctx, outer, 6, 12, 18, 'rgba(0,0,0,.16)');
  castShadow(ctx, outer, 16, 26, 38, 'rgba(0,0,0,.10)');

  if (frameImg) drawFrameRingToContext(ctx, frameImg, W, H, fp, 1);

  // base fill
  timber();
  fillBox(ctx, opening, 'rgba(0,0,0,.58)');

  // floor, with the work's cast shadow on it
  ctx.save();
  scaleAbout(ctx, cx, cy, f.sFloor);
  ctx.beginPath();
  ctx.rect(opening.x, opening.y, opening.w, opening.h);
  ctx.clip();
  timber();
  ctx.save();
  ctx.translate(opening.x + opening.w * 0.15, opening.y + opening.h * 0.12);
  ctx.scale(opening.w * 1.4, opening.h * 1.2);
  const floorShade = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
  floorShade.addColorStop(0, 'rgba(0,0,0,.04)');
  floorShade.addColorStop(0.55, 'rgba(0,0,0,.18)');
  floorShade.addColorStop(1, 'rgba(0,0,0,.40)');
  ctx.fillStyle = floorShade;
  ctx.fillRect(-1, -1, 2, 2);
  ctx.restore();
  const cast = { x: opening.x + gap + Math.round(d * 0.08), y: opening.y + gap + Math.round(d * 0.16), w: artWpx.value, h: artHpx.value };
  const kFloor = EXPORT_SCALE * f.sFloor;
  outerShadow(ctx, cast, Math.round(d * 0.30), Math.round(d * 0.55), Math.round(d * 1.35), Math.round(d * 0.10), 'rgba(18,9,3,.26)', kFloor);
  outerShadow(ctx, cast, Math.round(d * 0.10), Math.round(d * 0.22), Math.round(d * 0.38), Math.round(-d * 0.12), 'rgba(18,9,3,.55)', kFloor);
  outerShadow(ctx, cast, 0, 0, Math.round(d * 0.30), Math.round(d * 0.02), 'rgba(18,9,3,.30)', kFloor);
  ctx.restore();

  // the four walls: trapezoids between the opening and the floor
  const wall = (pts: Pt[], shade: CanvasGradient) => {
    ctx.save();
    ctx.beginPath();
    tracePolygon(ctx, pts.map((p) => ({ x: opening.x + p.x, y: opening.y + p.y })));
    ctx.clip();
    timber();
    fillBox(ctx, opening, shade);
    ctx.restore();
  };
  const { fL, fT, fR, fB } = f;
  const ow = opening.w, oh = opening.h, ox = opening.x, oy = opening.y;
  wall([{ x: 0, y: 0 }, { x: fL, y: fT }, { x: fL, y: fB }, { x: 0, y: oh }],
    twoStopGradient(ctx, ox, 0, ox + fL, 0, 'rgba(0,0,0,.38)', 'rgba(0,0,0,.62)'));
  wall([{ x: ow, y: 0 }, { x: ow, y: oh }, { x: fR, y: fB }, { x: fR, y: fT }],
    twoStopGradient(ctx, ox + ow, 0, ox + fR, 0, 'rgba(0,0,0,.48)', 'rgba(0,0,0,.68)'));
  wall([{ x: 0, y: 0 }, { x: ow, y: 0 }, { x: fR, y: fT }, { x: fL, y: fT }],
    twoStopGradient(ctx, 0, oy, 0, oy + fT, 'rgba(0,0,0,.45)', 'rgba(0,0,0,.64)'));
  wall([{ x: 0, y: oh }, { x: fL, y: fB }, { x: fR, y: fB }, { x: ow, y: oh }],
    twoStopGradient(ctx, 0, oy + oh, 0, oy + fB, 'rgba(255,246,232,.14)', 'rgba(0,0,0,.46)'));

  // the work, scaled to the depth it hangs at
  const art = { x: artLeftPx.value, y: artTopPx.value, w: artWpx.value, h: artHpx.value };
  ctx.save();
  scaleAbout(ctx, cx, cy, f.sWork);
  fillBox(ctx, art, '#2a1a10');
  drawImageCover(ctx, artImg, art);
  insetShadow(ctx, art, 0, Math.max(2, Math.round(gap * 0.30)), Math.max(4, Math.round(gap * 0.6)), 0, 'rgba(0,0,0,.18)', EXPORT_SCALE * f.sWork);
  ctx.restore();

  // the arris at the opening
  outerShadow(ctx, opening, 0, 0, 1, 0.5, 'rgba(0,0,0,.30)');

  drawFlatCallouts(ctx, W, H);
}

// One triangle of the artwork, affine-mapped from image pixels (s*) onto the canvas (d*).
function drawImageTriangle(ctx: CanvasRenderingContext2D, img: HTMLImageElement, s0: Pt, s1: Pt, s2: Pt, d0: Pt, d1: Pt, d2: Pt) {
  const det = (s1.x - s0.x) * (s2.y - s0.y) - (s2.x - s0.x) * (s1.y - s0.y);
  if (!det) return;
  const a = ((d1.x - d0.x) * (s2.y - s0.y) - (d2.x - d0.x) * (s1.y - s0.y)) / det;
  const c = ((d2.x - d0.x) * (s1.x - s0.x) - (d1.x - d0.x) * (s2.x - s0.x)) / det;
  const b = ((d1.y - d0.y) * (s2.y - s0.y) - (d2.y - d0.y) * (s1.y - s0.y)) / det;
  const d = ((d2.y - d0.y) * (s1.x - s0.x) - (d1.y - d0.y) * (s2.x - s0.x)) / det;
  // Grow the clip a little past the triangle: neighbours are anti-aliased separately, and an exact
  // fit leaves a hairline of whatever is underneath along every shared edge.
  const gx = (d0.x + d1.x + d2.x) / 3, gy = (d0.y + d1.y + d2.y) / 3;
  const grow = (p: Pt) => {
    const vx = p.x - gx, vy = p.y - gy, len = Math.hypot(vx, vy) || 1;
    return { x: p.x + (vx / len) * 0.75, y: p.y + (vy / len) * 0.75 };
  };
  ctx.save();
  ctx.beginPath();
  tracePolygon(ctx, [grow(d0), grow(d1), grow(d2)]);
  ctx.clip();
  ctx.transform(a, b, c, d, d0.x - a * s0.x - c * s0.y, d0.y - b * s0.x - d * s0.y);
  ctx.drawImage(img, 0, 0);
  ctx.restore();
}

// STRETCHED CANVAS — the turned 3D box, projected with the same maths that places its callouts.
function drawStretchedCanvas(ctx: CanvasRenderingContext2D, artImg: HTMLImageElement | null) {
  const W = artWpx.value, H = artHpx.value, D = stretchDepthPx.value;
  const P = stretchPerspectivePx.value;
  const originY = (PERSPECTIVE_ORIGIN_Y - 0.5) * H;
  // box-centred 3D point -> the artwork box's own local 2D coordinates
  const at = (x: number, y: number, z: number): Pt => {
    const p = projectPoint(x, y, z, P, STRETCH_PITCH, -STRETCH_YAW, originY);
    return { x: W / 2 + p.sx, y: H / 2 + p.sy };
  };
  const l = -W / 2, r = W / 2, t = -H / 2, b = H / 2;
  const front = [at(l, t, 0), at(r, t, 0), at(r, b, 0), at(l, b, 0)];
  const rightSide = [at(r, t, 0), at(r, t, -D), at(r, b, -D), at(r, b, 0)];
  const topSide = [at(l, t, -D), at(r, t, -D), at(r, t, 0), at(l, t, 0)];
  // all wound the same way, so the three faces fill as one silhouette
  const sameWinding = (q: Pt[]) => {
    let area = 0;
    q.forEach((p, i) => {
      const n = q[(i + 1) % q.length]!;
      area += p.x * n.y - n.x * p.y;
    });
    return area < 0 ? [...q].reverse() : q;
  };
  const silhouette = () => [front, rightSide, topSide].forEach((q) => tracePolygon(ctx, sameWinding(q)));

  castShadow(ctx, silhouette, 20, 30, 42, 'rgba(0,0,0,0.18)');
  castShadow(ctx, silhouette, 6, 9, 10, 'rgba(0,0,0,0.16)');
  castShadow(ctx, silhouette, 1, 1, 1, 'rgba(0,0,0,0.16)');

  const fillFace = (q: Pt[], style: string | CanvasGradient) => {
    ctx.beginPath();
    tracePolygon(ctx, q);
    ctx.fillStyle = style;
    ctx.fill();
  };
  // the returns: raw canvas off-white, the right one very slightly toned
  fillFace(topSide, '#F9F8F4');
  fillFace(rightSide, '#F9F8F4');
  const nearMid = at(r, 0, 0), farMid = at(r, 0, -D);
  fillFace(rightSide, twoStopGradient(ctx, nearMid.x, nearMid.y, farMid.x, farMid.y, 'rgba(0,0,0,0.015)', 'rgba(0,0,0,0.045)'));

  // front face: the artwork, warped onto the turned face a small patch at a time
  fillFace(front, '#ffffff');
  const crop = coverCrop(artImg, W, H);
  if (artImg && crop) {
    ctx.save();
    ctx.beginPath();
    tracePolygon(ctx, front);
    ctx.clip();
    const N = 14;
    for (let i = 0; i < N; i++) {
      for (let j = 0; j < N; j++) {
        const u0 = i / N, u1 = (i + 1) / N, v0 = j / N, v1 = (j + 1) / N;
        const src = (u: number, v: number): Pt => ({ x: crop.x + u * crop.w, y: crop.y + v * crop.h });
        const dst = (u: number, v: number): Pt => at(l + u * W, t + v * H, 0);
        drawImageTriangle(ctx, artImg, src(u0, v0), src(u1, v0), src(u1, v1), dst(u0, v0), dst(u1, v0), dst(u1, v1));
        drawImageTriangle(ctx, artImg, src(u0, v0), src(u1, v1), src(u0, v1), dst(u0, v0), dst(u1, v1), dst(u0, v1));
      }
    }
    ctx.restore();
  }
  // edge line where the face meets the sides (inset 0 0 0 1px)
  ctx.save();
  ctx.beginPath();
  tracePolygon(ctx, front);
  ctx.clip();
  ctx.strokeStyle = 'rgba(0,0,0,0.12)';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();

  // dimension callouts
  const arrows = [
    { a: stretchWidthArrow.value, width: 1.8 },
    { a: stretchHeightArrow.value, width: 1.2 },
    { a: stretchDepthArrow.value, width: 1.2 },
  ];
  for (const { a, width } of arrows) {
    strokeSegments(ctx, [a, a.tick1, a.tick2, a.tick3, a.tick4].map(
      (s): [number, number, number, number] => [s.x1, s.y1, s.x2, s.y2]
    ), width);
  }
  drawLabel(ctx, stretchWidthLabel.value, stretchWidthArrow.value.labelPos.x, stretchWidthArrow.value.labelPos.y, 'center');
  drawLabel(ctx, stretchHeightLabel.value, stretchHeightArrow.value.mid.x + 8, stretchHeightArrow.value.mid.y, 'left');
  drawLabel(ctx, stretchDepthLabel.value, stretchDepthArrow.value.labelPos.x, stretchDepthArrow.value.labelPos.y, 'center');
}

// The whole preview box as a PNG, laid out like the html-to-image export: the box's own size at 2x,
// the panel colour behind, and the frame centred in it.
async function renderPreviewOnCanvas(el: HTMLElement): Promise<Blob | null> {
  const boxW = el.offsetWidth, boxH = el.offsetHeight;
  if (!boxW || !boxH) return null;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(boxW * EXPORT_SCALE);
  canvas.height = Math.round(boxH * EXPORT_SCALE);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const load = async (url?: string) => {
    if (!url) return null;
    const img = await loadImageElement(await toDataUrl(url));
    return img.naturalWidth > 0 && img.naturalHeight > 0 ? img : null;
  };
  const [frameImg, artImg] = await Promise.all([
    isStretchMode.value ? null : load(frameImageUrl.value),
    load(props.artworkImageSrc),
  ]);

  ctx.scale(EXPORT_SCALE, EXPORT_SCALE);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#f8f9fa';
  ctx.fillRect(0, 0, boxW, boxH);
  ctx.font = `600 13px ${window.getComputedStyle(el).fontFamily || 'sans-serif'}`;

  if (isStretchMode.value) {
    ctx.translate((boxW - artWpx.value) / 2 + stretchOffsetX.value, (boxH - artHpx.value) / 2 + stretchOffsetY.value);
    drawStretchedCanvas(ctx, artImg);
  } else {
    ctx.translate((boxW - outerWpx.value) / 2, (boxH - outerHpx.value) / 2);
    if (isFloater.value) drawFloaterFrame(ctx, frameImg, artImg);
    else drawClassicFrame(ctx, frameImg, artImg);
  }

  return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
}

async function downloadPreview() {
  const el = containerRef.value;
  if (!el || isDownloading.value) return;
  isDownloading.value = true;

  const ios = isIOS();
  const filename = `${isStretchMode.value ? 'stretched-canvas' : 'frame'}-preview.png`;

  try {
    if (ios) {
      // Safari / iOS: redraw the preview on a 2D canvas (see renderPreviewOnCanvas). Anything going
      // wrong in there falls through to the html-to-image pipeline below rather than failing outright.
      let blob: Blob | null = null;
      try {
        blob = await renderPreviewOnCanvas(el);
      } catch (e) {
        console.warn('Canvas export failed, falling back to html-to-image', e);
      }
      if (blob) {
        // Same direct download as every other browser. The URL is kept alive longer than elsewhere:
        // iOS asks the buyer to confirm the download first and only reads the file after that.
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
        return;
      }
    }

    // Android, Windows, Chrome, Firefox & Stretch mode: standard html-to-image pipeline (100% UNCHANGED)
    const { toBlob, toPng } = await import('html-to-image');
    const inlined = await toDataUrl(frameImageUrl.value);
    
    let inlinedArtworkSrc: string | null = null;
    if (props.artworkImageSrc) {
      try {
        inlinedArtworkSrc = await toDataUrl(props.artworkImageSrc);
      } catch { /* non-fatal */ }
    }

    if (ios) {
      await preloadImage(inlined);
      if (inlinedArtworkSrc) await preloadImage(inlinedArtworkSrc);
      iosCapturing.value = true;
    }
    inlinedFrameImage.value = inlined;
    if (inlinedArtworkSrc) inlinedArtworkImage.value = inlinedArtworkSrc;

    await nextTick();
    if (ios) {
      await delay(150);
      await waitForImagesToDecode(el);
    }

    // The preview box is padded unevenly on purpose (extra room on the right/top for the dimension
    // labels and the wall shadow), which leaves the frame off-centre in the saved image. For the
    // capture only, spread the same total padding evenly so the frame lands in the middle. The
    // override applies to the captured copy of the box, never to the live preview.
    const cs = window.getComputedStyle(el);
    const px = (v: string) => parseFloat(v) || 0;
    const padX = (px(cs.paddingLeft) + px(cs.paddingRight)) / 2;
    const padY = (px(cs.paddingTop) + px(cs.paddingBottom)) / 2;
    const opts = {
      pixelRatio: 2, cacheBust: true, skipFonts: true, backgroundColor: '#f8f9fa', filter: skipComments,
      style: { paddingLeft: padX + 'px', paddingRight: padX + 'px', paddingTop: padY + 'px', paddingBottom: padY + 'px' },
    };
    await toPng(el, opts);
    await toPng(el, opts);

    let blob: Blob | null = null;
    if (typeof toBlob === 'function') {
      blob = await toBlob(el, opts);
    }
    if (!blob) {
      const dataUrl = await toPng(el, opts);
      blob = await (await fetch(dataUrl)).blob();
    }
    
    // Standard anchor download
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), ios ? 5 * 60 * 1000 : 30000);
  } catch (e) {
    console.error('Could not download the preview', e);
    alert('Sorry, the preview could not be downloaded. Please try again.');
  } finally {
    inlinedArtworkImage.value = null;
    inlinedFrameImage.value = null;
    iosFrameRing.value = null;
    iosCapturing.value = false;
    isDownloading.value = false;
  }
}
// Snapped to a 4px step. A preview's HEIGHT is derived from the width measured here, so anything
// that makes the width wobble — a scrolling ancestor gaining or losing its scrollbar, or just the
// fractional widths a ResizeObserver reports — can feed back as a visible shimmer. Quantising means
// noise below the step changes nothing at all.
const availableW = computed(() => {
  const raw = (containerWidth.value > 0 ? containerWidth.value : 336) - 16;
  return Math.max(120, Math.floor(raw / 4) * 4);
});

// containerRef's CSS height (see the template) is the OUTER box size — padding is reserved space
// for the dimension labels/arrows and the wall shadow, not room the frame itself gets to use.
// MAX_PREVIEW_H alone double-counts that padding as if it were available content height, which is
// exactly what pushed the frame into the label's reserved space and clipped it. Measuring the
// element's actual content-box height (same mechanism availableW already uses for width) gives
// the real number — MAX_PREVIEW_H minus whatever padding the current breakpoint applies — without
// hand-duplicating the responsive pl/pr/pt/pb values here. Safe to measure now specifically
// because the element's height is fixed by CSS rather than driven by this content, so there's no
// circular "available space = however much I already used" loop.
const availableH = computed(() => {
  const raw = (containerHeight.value > 0 ? containerHeight.value : MAX_PREVIEW_H) - 16;
  return Math.max(120, Math.floor(raw / 4) * 4);
});

// A stretched ARTWORK is drawn to fill the preview rather than at a fixed px-per-inch, so a small
// canvas isn't a thumbnail. Its bar depth does not follow that scale — it holds PX_PER_INCH like
// every other chosen size (see stretchDepthPx). The cap keeps a postcard from filling the panel.
const STRETCH_MAX_PX_PER_INCH = PX_PER_INCH * 3;
// Perspective proportional to the object, not a fixed distance: a constant 1600px is a telephoto
// lens on a 150px object (no convergence at all — it renders isometric and reads flat) and close
// to a fisheye on a 900px one. x5 is the long-ish lens a product shot is taken on: the near edge
// grows just enough to sell the turn, without bowing the painting.
const stretchPerspectivePx = computed(() => Math.max(900, Math.round(artWpx.value * 5)));

// --- CENTRING A TURNED BOX ---
// A box rotated with rotateX/rotateY under a CSS perspective is NOT centred on its own layout
// box: the far edges extend beyond it in whatever direction the turn and the perspective's own
// near-edge enlargement push them, so the composition drifts off-centre. Rather than fudge a
// constant per case, project all eight corners of the WxHxD box exactly the way the browser will
// (same rotation order, same perspective divide) and re-centre on the bounding box that comes
// back. Shared by both the stretched canvas and the floater tray below — same box-in-perspective
// maths, different W/H/D/turn per caller.
// rotateXDeg/rotateYDeg are the LITERAL degrees passed to the CSS transform (sign and all) —
// callers don't pass an abstract "yaw" that gets negated in here, they pass exactly what
// `rotateX(...)  rotateY(...)` says in the template, so this can never drift out of sync with it.
function projectTurnedBoxBounds(W: number, H: number, D: number, P: number, rotateXDeg: number, rotateYDeg: number, originYFrac: number) {
  const a = rotateXDeg * RAD;
  const b = rotateYDeg * RAD;
  // CSS applies `rotateX(a) rotateY(b)` as Rx(a) . Ry(b) . point.
  const cosA = Math.cos(a), sinA = Math.sin(a);
  const cosB = Math.cos(b), sinB = Math.sin(b);
  // Perspective origin in the same centred coordinates as the corners.
  const oy = (originYFrac - 0.5) * H;

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const x of [-W / 2, W / 2]) {
    for (const y of [-H / 2, H / 2]) {
      for (const z of [0, -D]) {
        const x1 = x * cosB + z * sinB;
        const z1 = -x * sinB + z * cosB;
        const y2 = y * cosA - z1 * sinA;
        const z2 = y * sinA + z1 * cosA;
        const k = P / (P - z2);
        const sx = x1 * k;
        const sy = oy + (y2 - oy) * k;
        if (sx < minX) minX = sx;
        if (sx > maxX) maxX = sx;
        if (sy < minY) minY = sy;
        if (sy > maxY) maxY = sy;
      }
    }
  }
  return { minX, maxX, minY, maxY };
}

const PERSPECTIVE_ORIGIN_Y = 0.45; // must match perspective-origin in the template

const stretchProjectedBounds = computed(() =>
  projectTurnedBoxBounds(artWpx.value, artHpx.value, stretchDepthPx.value, stretchPerspectivePx.value, STRETCH_PITCH, -STRETCH_YAW, PERSPECTIVE_ORIGIN_Y));

// Shift by half the bounding box's own offset, so what the viewer sees is centred in the panel.
const stretchOffsetX = computed(() => {
  const { minX, maxX } = stretchProjectedBounds.value;
  return -Math.round(((minX + maxX) / 2) * 100) / 100;
});
const stretchOffsetY = computed(() => {
  const { minY, maxY } = stretchProjectedBounds.value;
  return -Math.round(((minY + maxY) / 2) * 100) / 100;
});

// --- DIMENSION ARROWS THAT TRACK THE TURNED BOX ---
// Same projection math as projectTurnedBoxBounds above, but for ONE point rather than a bounding
// box, so the dimension lines can run parallel to the box's actual on-screen edges instead of a
// flat, unrotated rectangle. Centred coordinates (x:-W/2..W/2, y:-H/2..H/2, z=0 for the front
// face) — same convention projectTurnedBoxBounds uses, so this only ever agrees with it.
function projectPoint(x: number, y: number, z: number, P: number, rotateXDeg: number, rotateYDeg: number, oy: number) {
  const a = rotateXDeg * RAD, b = rotateYDeg * RAD;
  const cosA = Math.cos(a), sinA = Math.sin(a);
  const cosB = Math.cos(b), sinB = Math.sin(b);
  const x1 = x * cosB + z * sinB;
  const z1 = -x * sinB + z * cosB;
  const y2 = y * cosA - z1 * sinA;
  const z2 = y * sinA + z1 * cosA;
  const k = P / (P - z2);
  return { sx: x1 * k, sy: oy + (y2 - oy) * k };
}

// The front face's 4 corners, converted from projectPoint's box-centred output into the SAME
// local coordinate system the perspective div uses (0,0 at its own top-left, W,H at its bottom-
// right) — the rotated box sits centred in that div by default transform-origin, so the centre
// of that local box (W/2, H/2) is where the rotation actually pivots.
const stretchFrontCorners = computed(() => {
  const W = artWpx.value, H = artHpx.value;
  const P = stretchPerspectivePx.value;
  const oy = (PERSPECTIVE_ORIGIN_Y - 0.5) * H;
  const toLocal = (x: number, y: number) => {
    const p = projectPoint(x, y, 0, P, STRETCH_PITCH, -STRETCH_YAW, oy);
    return { x: W / 2 + p.sx, y: H / 2 + p.sy };
  };
  return {
    tl: toLocal(-W / 2, -H / 2),
    tr: toLocal(W / 2, -H / 2),
    bl: toLocal(-W / 2, H / 2),
    br: toLocal(W / 2, H / 2),
  };
});

// A double-headed arrow between two already-placed points: tip at p1 points AWAY from p2 (and
// vice versa), which is what "<----->" means — barbs at each end trail back TOWARD the opposite
// end, rotated by a small angle, rather than trailing further outward past the tip (which draws
// ">----<", arrows pointing at each other, the exact reverse of a dimension arrow).
function doubleHeadedArrow(p1: { x: number; y: number }, p2: { x: number; y: number }) {
  const dx = p2.x - p1.x, dy = p2.y - p1.y;
  const len = Math.max(1, Math.hypot(dx, dy));
  const ux = dx / len, uy = dy / len; // unit vector p1 -> p2
  const tickLen = 7;
  const tickAngle = (25 * Math.PI) / 180;
  const rot = (vx: number, vy: number, ang: number) => ({
    x: vx * Math.cos(ang) - vy * Math.sin(ang),
    y: vx * Math.sin(ang) + vy * Math.cos(ang),
  });
  // Tip at p1 points along -u (away from p2) — its barbs trail back along +u (toward p2).
  const t1a = rot(ux, uy, tickAngle), t1b = rot(ux, uy, -tickAngle);
  // Tip at p2 points along +u (away from p1) — its barbs trail back along -u (toward p1).
  const t2a = rot(-ux, -uy, tickAngle), t2b = rot(-ux, -uy, -tickAngle);
  return {
    x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y,
    dir: { x: ux, y: uy },
    mid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
    tick1: { x1: p1.x, y1: p1.y, x2: p1.x + t1a.x * tickLen, y2: p1.y + t1a.y * tickLen },
    tick2: { x1: p1.x, y1: p1.y, x2: p1.x + t1b.x * tickLen, y2: p1.y + t1b.y * tickLen },
    tick3: { x1: p2.x, y1: p2.y, x2: p2.x + t2a.x * tickLen, y2: p2.y + t2a.y * tickLen },
    tick4: { x1: p2.x, y1: p2.y, x2: p2.x + t2b.x * tickLen, y2: p2.y + t2b.y * tickLen },
  };
}

// A dimension line running parallel to one edge of the (possibly tilted) box, offset outward by
// `offset` px. `from`/`to` must be given in CLOCKWISE traversal order round the box (e.g.
// tl->tr for the top edge, tr->br for the right edge): the outward normal is always the edge
// vector rotated 90° clockwise, which only points away from the box interior for that winding.
// `insetFrom`/`insetTo` pull each end back along the edge itself (toward the other end), used to
// clear the shared corner when two dimension lines meet there — see the width/height arrows below,
// which both touch the box's "tr" corner and would otherwise crowd each other's tips at any turn
// angle where their outward normals aren't near-perpendicular.
function edgeDimensionLine(from: { x: number; y: number }, to: { x: number; y: number }, offset: number, insetFrom = 0, insetTo = 0) {
  const dx = to.x - from.x, dy = to.y - from.y;
  const len = Math.max(1, Math.hypot(dx, dy));
  const ux = dx / len, uy = dy / len; // unit vector from -> to, along the edge
  const nx = dy / len, ny = -dx / len; // outward unit normal (90° clockwise from the edge)
  // On a small or heavily foreshortened edge, a fixed corner inset can eat most of the line and
  // leave the arrow looking collapsed to a stub. Scale both insets down together (same ratio, so
  // the arrow stays centred on the edge) whenever they'd otherwise claim more than half of it.
  const insetScale = Math.min(1, (len * 0.5) / Math.max(1, insetFrom + insetTo));
  const fromIn = { x: from.x + ux * insetFrom * insetScale, y: from.y + uy * insetFrom * insetScale };
  const toIn = { x: to.x - ux * insetTo * insetScale, y: to.y - uy * insetTo * insetScale };
  const p1 = { x: fromIn.x + nx * offset, y: fromIn.y + ny * offset };
  const p2 = { x: toIn.x + nx * offset, y: toIn.y + ny * offset };
  const arrow = doubleHeadedArrow(p1, p2);
  return {
    ...arrow,
    normal: { x: nx, y: ny },
    // A little further out than the line itself, so the number sits clear of it rather than
    // sitting right on top of the stroke.
    labelPos: { x: arrow.mid.x + nx * 16, y: arrow.mid.y + ny * 16 },
  };
}

const STRETCH_DIM_OFFSET = 16;
// Both arrows meet at the box's "tr" corner. Insetting each one's end there by a fixed px amount
// (along its own edge, not the screen axes) keeps their tips clear of each other regardless of the
// turn angle — trimming by absolute screen distance wouldn't, since a normal-only offset leaves
// them as close as the two edges' angle at that corner happens to make them.
const STRETCH_DIM_CORNER_GAP = 10;
const stretchWidthArrow = computed(() => edgeDimensionLine(stretchFrontCorners.value.tl, stretchFrontCorners.value.tr, STRETCH_DIM_OFFSET, STRETCH_DIM_CORNER_GAP, STRETCH_DIM_CORNER_GAP));
// Offset enough to clear the visible right return face (which grows with bar depth) plus a fixed margin.
const stretchHeightArrow = computed(() => edgeDimensionLine(stretchFrontCorners.value.tr, stretchFrontCorners.value.br, STRETCH_DIM_OFFSET + stretchDepthApparentPx.value + 0, STRETCH_DIM_CORNER_GAP, 0));

// The bar depth callout — the third dimension a stretch has that a flat frame doesn't. The return
// face's own CSS width (stretchDepthPx) is pre-divided by sin(yaw) so the true 3D rotation
// foreshortens it back down to the intended on-screen sliver (see stretchDepthPx above); multiplying
// by that same sin(yaw) here undoes exactly that pre-division, giving the sliver's actual apparent
// width on screen without having to re-run the full 3D projection for a point that isn't on the
// front face. Drawn as a small horizontal arrow under the box's bottom-right corner, where that
// sliver is visible, rather than trying to track its true (heavily foreshortened) parallelogram.
const STRETCH_DEPTH_GAP = 14;
const stretchDepthApparentPx = computed(() => stretchDepthPx.value * Math.sin(STRETCH_YAW * RAD));
const stretchDepthArrow = computed(() => {
  const br = stretchFrontCorners.value.br;
  const p1 = { x: br.x, y: br.y + STRETCH_DEPTH_GAP };
  const p2 = { x: br.x + stretchDepthApparentPx.value, y: br.y + STRETCH_DEPTH_GAP };
  const arrow = doubleHeadedArrow(p1, p2);
  return { ...arrow, labelPos: { x: arrow.mid.x, y: arrow.mid.y + 14 } };
});
const stretchDepthLabel = computed(() => fmtDimension(stretchDepthIn.value));

// One scale for the whole assembly — artwork, frame, mount and floater gap all drawn at the same
// px-per-inch, picked so the TOTAL outer size (everything added together) fits the available
// width/height. Previously the frame/mount held a fixed 13px/inch no matter what, and only the
// artwork gave way when space ran short — a wide moulding plus a wide mount could leave a large
// piece looking like a sliver of art inside a disproportionately thick frame. Now a bigger
// front-width/mount shrinks the WHOLE preview together, proportionally, the same way stretch mode
// (which never had a frame to protect) already scaled itself.
// SAFETY_PX below the raw fit, not right at it: outerWpx/outerHpx are the SUM of several
// independently-rounded pieces (artWpx + 2×framePx + 2×mountPx + 2×floaterPx), and rounding each
// one separately can push the total a pixel or two past what a single combined round() would give
// — this margin absorbs that so the rendered box can't tip over availableW/availableH. The actual
// fix for the dialog-overflow bug this surfaced is in the three framing-panel__preview/-inner CSS
// rules (min-width: 0 + overflow: hidden) — those are what stop a bigger box from inflating its
// ancestors; this margin is just insurance against the rounding slop on top of that.
const SAFETY_PX = 4;
const uniformScale = computed(() => {
  if (isStretchMode.value) {
    return Math.max(
      0.5,
      Math.min(STRETCH_MAX_PX_PER_INCH, stretchFitScaleW.value, stretchFitScaleH.value)
    );
  }
  // No PX_PER_INCH ceiling here on purpose (removed per request) — the drawing now always scales
  // up to fill whichever of width/height is the binding constraint, regardless of the artwork's
  // real-world size. A small piece in a big box will render larger than true-to-life.
  return Math.max(
    0.5,
    Math.min(
      (availableW.value - SAFETY_PX) / Math.max(1, declaredOuterW.value),
      (availableH.value - SAFETY_PX) / Math.max(1, declaredOuterH.value)
    )
  );
});

// px at the shared scale — artwork and frame/mount/floater are no longer split, so both resolve
// to the same function. Kept as two names since callers already read as "this is an artwork
// dimension" vs "this is a chosen build dimension", which is still true even though the number
// behind them is now identical.
const pxArt = (v: number) => Math.round(v * uniformScale.value * 100) / 100;
const pxFrame = (v: number) => Math.round(v * uniformScale.value * 100) / 100;

// Fixed real-world border thicknesses (frame/mount/gap), in px at the chosen scale — these
// stay physically accurate regardless of any photo-aspect adjustment below. Rounded to a whole
// pixel, not left at pxFrame's fractional precision: this value is used BOTH as a CSS
// border-width (the frame face) AND as the left/top position of separate sibling elements (the
// tray floor/walls in the floater build, the mount in the default build) that are meant to sit
// exactly flush against that border. Border-image painting and a transformed sibling's position
// round sub-pixels through different paths in the browser's rasterizer, so a fractional value
// like 19.53px can paint flush at some sizes and leave a hairline gap — showing the page
// background through it as a stray white line — at others, which is exactly what got reported:
// present at the default frame width, gone at other widths. A whole-pixel value removes the
// fractional remainder that path-dependent rounding needs to disagree on.
const framePx = computed(() => Math.round(pxFrame(frameW.value)));
const mountPx = computed(() => Math.round(pxFrame(mountW.value)));
const floaterPx = computed(() => Math.round(pxFrame(floaterW.value)));
const gapPx = computed(() => isCanvas.value ? floaterPx.value : 0);
// how far the spacer sets the artwork back, expressed as shadow depth in px at this scale
const depthPx = computed(() => Math.max(3, Math.round(uniformScale.value * 0.45)));
// Contact shadow the canvas throws into the recess. This is what makes it read as FLOATING in the
// tray rather than stuck flat to the back of it — an object with no shadow behind it looks glued
// down. Measured off the real frame: the shadow covers ~24px of the 45px bottom wall (a bit over
// half) and is rgb(91,57,30) against a lit wall of rgb(209,177,149), so it is warm and fairly
// tight, not a wide soft blur. The colour is a dark brown rather than black for the same reason the
// recess map is per-channel — a black shadow over wood goes grey and reads as dirt.
// Offset is down and slightly right, matching the light direction the wall brightnesses imply
// (left wall 51% vs right wall 43% — light comes from the left).
const contactY = computed(() => Math.max(2, Math.round(gapPx.value * 0.34)));
const contactX = computed(() => Math.max(1, Math.round(gapPx.value * 0.12)));
const contactBlur = computed(() => Math.max(3, Math.round(gapPx.value * 0.34)));

// NOTE: there used to be a light "canvas edge" ring (#d8cdb4) drawn around the artwork here, on the
// theory that the canvas wrapping the stretcher catches light in the reveal. In practice it read as
// a thin yellow line against the wall and the client flagged it — in the reference photo the
// painting meets the wall as a soft shadow, not a light line. Don't reintroduce it.
const borderPx = computed(() => framePx.value + gapPx.value + mountPx.value);

// Whole-pixel for the exact same reason framePx/mountPx/floaterPx are above: this feeds outerWpx/
// outerHpx below, which IS the width/height of the div the frame's border-image paints onto. A
// fractional box size is the other half of the same "hairline gap at the mitred corner" bug — it
// showed up depending on which container width the preview happened to measure (the reported case:
// present in one place, not another), which is exactly what a leftover fractional remainder here
// would produce.
const artWpx = computed(() => Math.max(1, Math.round(pxArt(artW.value))));
const artHpx = computed(() => Math.max(1, Math.round(pxArt(artHFit.value))));

const artLeftPx = computed(() => borderPx.value);
const artTopPx = computed(() => borderPx.value);

const outerWpx = computed(() => artWpx.value + 2 * borderPx.value);
const outerHpx = computed(() => artHpx.value + 2 * borderPx.value);

// The bar depth. It is the one thing the Stretch build lets the buyer choose, so like a frame,
// mount or floater gap it has to read the same size on every artwork — and "read" means what is
// VISIBLE. A frame is seen face-on, so its 13px is all on screen; the bar is a side turned away by
// STRETCH_YAW, so only depth * sin(yaw) of it shows (~47% at 28deg). Drawing it at a flat 13px/in
// therefore showed a 1" bar as a ~6px sliver. So the depth is sized for its on-screen width: the
// white side shows ~13px per inch, i.e. 1" ~ 13px and 2" ~ 26px on screen, whatever the artwork.
// Dividing by sin(yaw) alone lands at ~11px: the bar runs AWAY from the camera, so perspective
// shrinks it by a steady ~15%. Checked with the exact projection used for centring below across
// 6x6in to 48x36in and 320-440px panels, it stays within 11.0-11.2px, so one constant corrects it.
// (Before all this the depth followed the artwork's fill scale, which made the same 1" bar anything
// from ~8px on a large canvas to 39px on a small one.)
const STRETCH_DEPTH_PERSPECTIVE_CORRECTION = 1.17;
const stretchDepthPx = computed(() =>
  Math.round((stretchDepthIn.value * PX_PER_INCH * STRETCH_DEPTH_PERSPECTIVE_CORRECTION
    / Math.sin(STRETCH_YAW * RAD)) * 100) / 100);

// --- FLOATER FRAME ---
// A floater is not a moulding laid over the work: it is a shallow tray the canvas hangs inside,
// with air all round it. Read off the client's reference photos, front to back that is
//   frame face  ->  the frame's inner wall  ->  an open slot  ->  the canvas edge  ->  the work
// and the depth comes almost entirely from how those four are lit, not from any bevel.
const isFloater = computed(() => isCanvas.value && gapPx.value > 0.5);

// A floater is drawn as a real 3D tray, not a stack of flat rings, because that is what it is —
// the reference photographs show you looking INTO a box, with all four inner walls converging.
// It used to sit dead head-on (no rotation at all), which had two problems: it read as flat/2D
// rather than as an object you could feel had depth, and a wall viewed exactly edge-on like that
// is a numerically degenerate case for a CSS 3D transform — right at the seam between the wall
// and its neighbours, the browser's sub-pixel rounding can leave a hairline gap that shows the
// page background through it (reported as a stray white line down one side). A small turn — the
// same technique the stretched canvas above uses — fixes both: every wall gets real on-screen
// width instead of a knife-edge, and the box reads as genuinely 3D. The turn is deliberately
// gentle (nowhere near STRETCH_YAW): a floater's depth is only ~1-3", read almost head-on in the
// reference photos, so a few degrees is enough to sell it without visibly skewing the artwork's
// own face inside it.
// Front view only (requested 2026-09-14): both turns are 0, so the floater is seen straight on.
// Used to be 12 / 6. If a hairline gap ever shows between walls again, that old note above is why —
// raise these a few degrees rather than touching anything else.
const FLOATER_YAW = 0;
const FLOATER_PITCH = 0;
// Signed the OPPOSITE way from the stretch canvas's turn on purpose: this file's own convention
// (see the wall gradients below, and the default frame branch's wall-shadow note) is light from
// the upper-left, so the LEFT wall and BOTTOM wall are the brighter ones. Turning the camera this
// way opens up more of those two rather than the darker right/top walls.
const floaterRotateXDeg = FLOATER_PITCH;
const floaterRotateYDeg = FLOATER_YAW;

const floaterDepthPx = computed(() => Math.max(8, Math.round(pxFrame(Number(specs.value.frameDepth) || 2))));
// How far the work sits below the frame's face. Per the client's reference photo it hangs high
// in the tray, just below the face — not resting on the floor, and not mid-depth either — with
// most of the depth showing as open air beneath it. (Was 0.42, which read as noticeably too deep;
// 0.16 puts it near the top of the depth, close to the front, while still leaving room for the
// cast shadow to fall onto the floor below it — that air gap is the whole reason a floater
// throws a shadow at all.)
const floaterSetbackPx = computed(() => Math.round(floaterDepthPx.value * 0.16));
// A wall opens up to R * depth / (perspective + depth). Too long a lens and the tray closes to a
// flat ring; this distance is what makes the walls read at the proportions the photos show.
const floaterPerspectivePx = computed(() => Math.round(outerWpx.value * 1.4));

// Same centring problem as the turned stretch canvas, same fix: project the box's 8 corners
// through the actual turn and perspective and re-centre on whatever bounding box comes back,
// rather than guess a constant. Origin stays dead centre (0.5) — unlike the stretch canvas this
// turn is small and symmetric-ish, so there's no strong reason to bias it.
const FLOATER_PERSPECTIVE_ORIGIN_Y = 0.5;
const floaterProjectedBounds = computed(() =>
  projectTurnedBoxBounds(outerWpx.value, outerHpx.value, floaterDepthPx.value, floaterPerspectivePx.value, floaterRotateXDeg, floaterRotateYDeg, FLOATER_PERSPECTIVE_ORIGIN_Y));
const floaterOffsetX = computed(() => {
  const { minX, maxX } = floaterProjectedBounds.value;
  return -Math.round(((minX + maxX) / 2) * 100) / 100;
});
const floaterOffsetY = computed(() => {
  const { minY, maxY } = floaterProjectedBounds.value;
  return -Math.round(((minY + maxY) / 2) * 100) / 100;
});

const floaterOpening = computed(() => ({
  left: framePx.value,
  top: framePx.value,
  width: outerWpx.value - 2 * framePx.value,
  height: outerHpx.value - 2 * framePx.value,
}));
// Flat (2D) drawing of the same tray for the PNG download only. The download pipeline cannot draw
// CSS 3D - it flattens every plane into a tiled slab of the frame texture - so while a capture is
// running the tray is drawn from plain 2D shapes instead. It is the same front view the live 3D tray
// shows (FLOATER_YAW/PITCH are 0): the floor and the work are scaled toward the centre by the same
// perspective factor a plane at that depth would get, and each wall is the trapezoid between the
// opening and the floor. If the tray is ever turned again, this front-on drawing no longer matches.
const floaterFlat = computed(() => {
  const p = floaterPerspectivePx.value;
  const cx = outerWpx.value / 2;
  const cy = outerHpx.value / 2;
  const o = floaterOpening.value;
  const sFloor = p / (p + floaterDepthPx.value);
  const sWork = p / (p + floaterSetbackPx.value);
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const fL = r2(cx + (o.left - cx) * sFloor - o.left);
  const fT = r2(cy + (o.top - cy) * sFloor - o.top);
  const fR = r2(cx + (o.left + o.width - cx) * sFloor - o.left);
  const fB = r2(cy + (o.top + o.height - cy) * sFloor - o.top);
  return {
    sFloor, sWork, fL, fT, fR, fB,
    // transform-origin (relative to a plane's own box) that lands on the frame's centre
    floorOrigin: `${cx - o.left}px ${cy - o.top}px`,
    workOrigin: `${cx - artLeftPx.value}px ${cy - artTopPx.value}px`,
    left: `polygon(0px 0px, ${fL}px ${fT}px, ${fL}px ${fB}px, 0px ${o.height}px)`,
    right: `polygon(${o.width}px 0px, ${o.width}px ${o.height}px, ${fR}px ${fB}px, ${fR}px ${fT}px)`,
    top: `polygon(0px 0px, ${o.width}px 0px, ${fR}px ${fT}px, ${fL}px ${fT}px)`,
    bottom: `polygon(0px ${o.height}px, ${fL}px ${fB}px, ${fR}px ${fB}px, ${o.width}px ${o.height}px)`,
  };
});
// How far (CSS px) the seam backing reaches past the opening, i.e. underneath the frame face. The
// backing's own edge is anti-aliased like any other, so when that edge sits exactly on the face's
// inner edge (it does, by design) the page can still show through the half-covered pixel there as a
// 1px pale line - which depends on the screen scale, so it comes and goes. Reaching under the opaque
// face moves that edge out of sight.
const FLOATER_BACKING_OVERSHOOT = 2;

// How far (CSS px) each tray wall reaches OUT beneath the frame face. A wall is hinged exactly on the
// face's inner edge; on screens whose scale is not a whole number (110% / 125% / 150% ...) the two
// edges round to different device pixels and a 1px gap opens between them, which shows whatever is
// behind as a thin line - pale on a dark frame, dark on a light one. Reaching under the (opaque,
// drawn-in-front) face closes that gap. The walls also sit 1px behind the face plane: their edge would
// otherwise lie exactly IN the face's plane and be drawn over it as a thin dark line.
const FLOATER_SEAM_OVERLAP = 1.5;

// How far (CSS px) the tray floor is grown past the opening on every side. Where a wall ends at the floor
// the two meet at a sub-pixel seam too; a floor that extends a little further BEHIND the walls means
// anything showing through that seam is floor (the same wood) rather than what is behind the tray.
const FLOATER_FLOOR_GROW = 4;

// Every surface inside the tray is the SAME timber as the face — see the tray note below.
const floaterTimber = computed(() => ({
  backgroundColor: '#6d4a2f',
  backgroundImage: `url(${frameImage.value})`,
  backgroundSize: `${outerWpx.value}px ${outerHpx.value}px`,
}));

const FRAME_COLOR_IMAGES: Record<string, string> = {
  polished: woodenPolishedFrame,
  white: paintedWhiteFrame,
  black: paintedBlackFrame,
};

const FRAME_COLOR_RECESS: Record<string, string> = {
  polished: woodenPolishedRecess,
  white: paintedWhiteRecess,
  black: paintedBlackRecess,
};

// 9-slice moulding — used for the frame ring itself
const frameImage = computed(() => {
  // Set only for the moment of a PNG download — see downloadPreview.
  if (inlinedFrameImage.value) return inlinedFrameImage.value;
  return frameImageUrl.value;
});
// Same pattern as inlinedFrameImage/frameImage above, for the artwork <img> — see downloadPreview.
// This used to be done by reaching into the live DOM and setting img.src directly, bypassing Vue
// entirely. That's fragile: Vue owns that element's src via the :src binding below, and if
// anything triggered a re-render of it during the capture's several async steps, Vue would reset
// it back to the original CDN url — silently undoing the swap right before the snapshot, which is
// exactly what an intermittent "sometimes the artwork/frame doesn't show" bug looks like. Routing
// it through a ref Vue itself manages removes that race entirely.
const inlinedArtworkImage = ref<string | null>(null);
const artworkImage = computed(() => inlinedArtworkImage.value || props.artworkImageSrc);
const frameImageUrl = computed(() => {
  if (specs.value.frameMaterial === 'Aluminium') return aluminiumFrame;
  if (specs.value.frameFinish === 'Natural Lacquer') return naturalLacquerFrame;
  return FRAME_COLOR_IMAGES[specs.value.frameColor as string] || FRAME_COLOR_IMAGES.polished;
});

// 9-slice of the same moulding in shadow. Kept, but NOT currently drawn: its per-side tinting
// (near-black top, light bottom) reads as a different colour of wood next to the face, which is
// what made the float gap look like a separate dark band. The tray draws frameImage instead.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const recessImage = computed(() => {
  if (specs.value.frameMaterial === 'Aluminium') return aluminiumRecess;
  if (specs.value.frameFinish === 'Natural Lacquer') return naturalLacquerRecess;
  return FRAME_COLOR_RECESS[specs.value.frameColor as string] || FRAME_COLOR_RECESS.polished;
});
</script>
